import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Play, Pause, RotateCcw, Check, Sparkles, Clock, Flame,
  Filter, AlertCircle, ChevronDown, ChevronRight, ExternalLink, BookOpen,
  Trophy, Plus, X, MoreHorizontal, History, Hash, Power,
  Calendar, Activity, Heart, RefreshCw, ArrowUpDown, Wrench, Share2, Award,
  Edit3, MessageSquare, HeartPulse, Dumbbell,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useProfile } from '../context/ProfileContext';
import { useAuth } from '../context/AuthContext';
import { exercises, sampleWorkout, getExerciseById, getPrescription, isBeneficialExercise, isBaselineExercise, DEFAULT_REST_SECONDS } from '../data/exercises';
import { programs, getProgram, getTemplate, getDefaultSchedule, getScheduleForWeek, DAYS, todayIdx, localISO } from '../data/programs';
import { detectOvertraining, painAdjustment, shouldShowPainCheck, readinessFromCapacity, localCapacityFallback, autoRegulationLevel, regionalRegulationLevel, compoundRegLevel } from '../utils/training-analytics';
import { getAffectedMuscleGroups, isExerciseRegionallyAffected } from '../data/amputationAdjacency';
import GearRecommendations from '../components/GearRecommendations';
import CoachTip from '../components/CoachTip';
import WidgetGate from '../components/WidgetGate';
import { useFeatureFlag } from '../context/FeatureFlagsContext';
import MilestoneShareCard from '../components/MilestoneShareCard';
import StreakWidget from '../components/StreakWidget';
import { computeAdherenceStreak, newlyCrossedStreakMilestone, detectProgramCompletion } from '../utils/streaks';
import {
  weightUnit, displayWeight, parseWeightInput, weightStepLb,
} from '../utils/units';
import AIWorkoutModal from '../components/AIWorkoutModal';
import HistoryClampBanner from '../components/HistoryClampBanner';
import TodaysAdjustments from '../components/TodaysAdjustments';
import { useTierState } from '../hooks/useTierState';
import { usePacing } from '../hooks/usePacing';
import PaywallModal from '../components/PaywallModal';
import AddExerciseSheet from '../components/AddExerciseSheet';
import EditProfileModal from '../components/EditProfileModal';
import ContextualPrompt from '../components/ContextualPrompt';
import StrengthEntryEditModal from '../components/StrengthEntryEditModal';

// ---------- Helpers ----------

const newId = (() => { let n = 0; return () => `s${Date.now()}_${++n}`; })();

const makeDefaultSets = (block, ex, level, history = [], regLevel = 'green', effectiveRegLevel = null) => {
  const p = getPrescription(ex.tier, level || 'intermediate');
  // Default set count: when the user has history, mirror last session's
  // *completed* set count (no phantom sets). When no history exists, fall back
  // to the program prescription. User can always "Add set" for an extra one.
  const lastTime = findLastTime(history, ex.id);
  const lastCount = lastTime?.sets?.length || 0;
  // A block with an explicit `sets` value (Full Body's fixed, duration-safe
  // volume — founder spec Jul 2026) is a HARD CAP, not just a fallback
  // default. History can only bring it DOWN from there (fewer sets than last
  // time is always safe — undershoot is fine), never push it back UP past
  // the slot's designed budget. Without this cap, a completed-set history for
  // this exercise ID logged on ANY OTHER day/program (Bench Press on Push
  // Day, say) would silently override Full Body's time-budget-safe default —
  // reintroducing the exact session-overrun risk the fixed-sets design
  // exists to prevent. Programs without a block-level `sets` (everything but
  // Full Body, for now) are unaffected — history still wins as before.
  let sets = block.sets != null
    ? (lastCount > 0 ? Math.min(lastCount, block.sets) : block.sets)
    : (lastCount || p.sets);
  // CI-9 Phase A: effectiveRegLevel is the compounded level for regionally-
  // affected exercises (worse-of systemic + regional, amber+amber → red).
  // For non-affected exercises it equals regLevel. The effective level drives
  // volume reduction so a compound bad day trims affected movements further.
  const appliedLevel = effectiveRegLevel || regLevel;
  if (appliedLevel === 'red') sets = Math.min(sets, 2);
  else if (appliedLevel === 'amber' && ex.tier > 1) sets = Math.max(2, sets - 1);
  // Inputs start EMPTY (no auto-prefill). Anchoring effect is real — pre-filled
  // last-week numbers psychologically cap today's effort. Per-set "↻ Last:
  // 135 × 8" quick-fill chips give the user the convenience back AS A
  // Default sets are intentionally EMPTY — the user fills weight + reps.
  // Block-level explicit overrides for `weight` still apply. `block.reps`
  // (often a prescription range like '6-8') is NOT pre-filled into the
  // input — instead, it's surfaced as a "Target" chip in the header (see
  // ExerciseRow) so the rep input stays always-numeric on mobile and the
  // logged value is always a clean integer.
  // DELIBERATE CHOICE rather than an app-imposed default.
  return Array.from({ length: sets }, () => ({
    id: newId(),
    parentId: null,
    type: 'normal',
    weight: block.weight ?? '',
    reps: '',
    completed: false,
  }));
};

// Returns a display label for the exercise's target prescription.
// For isometric exercises (ex.isometric === true) returns a hold-time string.
// For exercises with coreReps overrides returns the appropriate rep/step label.
// Returns null when neither applies (caller falls back to block.reps / standard PRESCRIPTIONS).
const getCoreRepsLabel = (ex, level) => {
  if (!ex?.coreReps) return null;
  const cr = ex.coreReps;
  const lvl = level || 'intermediate';
  const val = cr[lvl];
  const unit = cr.unit;
  if (ex.isometric) {
    return unit === 'seconds/side' ? `${val}s/side` : `${val}s hold`;
  }
  if (Array.isArray(val)) {
    const range = val[0] === val[1] ? `${val[0]}` : `${val[0]}–${val[1]}`;
    if (unit === 'per side') return `${range} per side`;
    if (unit === 'steps/side') return `${range} steps/side`;
    return `${range} reps`;
  }
  return null;
};

// Find user's last logged performance for this exercise refId from history
const findLastTime = (history, refId) => {
  for (const h of history) {
    if (!h.exercises) continue;
    const match = h.exercises.find((e) => e.refId === refId);
    if (match && match.sets && match.sets.length > 0) {
      const done = match.sets.filter((s) => s.completed);
      if (done.length === 0) continue;
      return {
        date: h.date,
        sets: done,
        // Surface the per-exercise note the user left last time — appears as a
        // small "Last note" hint when they train this lift again.
        note: (match.note || '').trim() || null,
      };
    }
  }
  return null;
};

const formatLastTime = (last, units = 'imperial') => {
  if (!last) return null;
  const repsList = last.sets.map((s) => s.reps).join(',');
  const weights = [...new Set(last.sets.map((s) => s.weight))];
  const u = weightUnit(units);
  const wStr = weights.length === 1
    ? `${displayWeight(weights[0], units)} ${u}`
    : `${displayWeight(Math.min(...weights), units)}-${displayWeight(Math.max(...weights), units)} ${u}`;
  const drops = last.sets.filter((s) => s.type === 'drop').length;
  const days = Math.floor((Date.now() - new Date(last.date).getTime()) / (1000 * 60 * 60 * 24));
  const when = days === 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`;
  return `${wStr} × ${repsList}${drops ? ` (${drops} drop)` : ''} · ${when}`;
};

// ---------- PR / Milestone detection ----------

// Epley 1RM estimator: 1RM ≈ weight × (1 + reps/30). Good enough for shareable
// milestones up to ~10 reps; gets noisy past that, which is fine since heavy-
// rep PRs are mostly an endurance signal anyway.
const estimate1RM = (weight, reps) => {
  const w = Number(weight) || 0;
  const r = Number(reps) || 0;
  if (w <= 0 || r <= 0) return 0;
  return w * (1 + r / 30);
};

// For an exercise, find the best estimated 1RM from a list of completed sets.
const bestE1RMFromSets = (sets) => {
  let best = 0;
  let bestSet = null;
  for (const s of sets || []) {
    if (!s.completed) continue;
    const e = estimate1RM(s.weight, s.reps);
    if (e > best) { best = e; bestSet = s; }
  }
  return { e1rm: best, set: bestSet };
};

// Detect PRs hit in today's workout. Returns list of:
//   { exerciseName, refId, weight, reps, prevBestE1RM, todayE1RM }
const detectPRs = (plan, setsByBlock, history) => {
  const prs = [];
  for (let i = 0; i < plan.length; i++) {
    const block = plan[i];
    const todaySets = (setsByBlock[i] || []).filter((s) => !s.parentId);
    const today = bestE1RMFromSets(todaySets);
    if (!today.set || today.e1rm <= 0) continue;
    // Best historical e1RM for this exercise
    let prevBest = 0;
    for (const h of history) {
      const match = h.exercises?.find((e) => e.refId === block.ex.id);
      if (!match) continue;
      const sess = bestE1RMFromSets(match.sets);
      if (sess.e1rm > prevBest) prevBest = sess.e1rm;
    }
    // Require non-trivial improvement (≥1 lb estimated) and at least 1 prior session.
    // Without a baseline, every first-ever lift would trigger a "PR" which dilutes the signal.
    if (prevBest > 0 && today.e1rm > prevBest + 1) {
      prs.push({
        exerciseName: block.ex.name,
        refId: block.ex.id,
        weight: today.set.weight,
        reps: today.set.reps,
        prevBestE1RM: Math.round(prevBest),
        todayE1RM: Math.round(today.e1rm),
      });
    }
  }
  return prs;
};

// Session-count milestones — celebrate the meaningful thresholds.
const SESSION_MILESTONES = [10, 25, 50, 100, 250, 500, 1000];
const detectSessionMilestone = (newSessionCount) => {
  return SESSION_MILESTONES.includes(newSessionCount) ? newSessionCount : null;
};

// ---------- Component ----------

const Strength = () => {
  const { profile, history, completeWorkout, update, markStreakCelebrated, updateStreakBestEver, addProgramCompletion, snoozeProgramCompletion, updateWorkout, removeWorkout } = useProfile();
  const { user } = useAuth();
  const { enabled: affiliatesEnabled } = useFeatureFlag('affiliates');
  const navigate = useNavigate();

  // Band popup — one-time nudge for no-equipment users.
  // Tracked in seenContextualPrompts so it never re-shows after dismiss.
  const seenPrompts = profile.seenContextualPrompts || {};
  const showBandPopup = affiliatesEnabled && !!profile.noEquipment && !seenPrompts['band-nudge'];
  const [bandPopupOpen, setBandPopupOpen] = useState(showBandPopup);
  const [bandsCardOpen, setBandsCardOpen] = useState(false);

  const dismissBandPopup = () => {
    update({ seenContextualPrompts: { ...seenPrompts, 'band-nudge': 'dismissed' } });
    setBandPopupOpen(false);
  };
  const openBandsFromPopup = () => {
    update({ seenContextualPrompts: { ...seenPrompts, 'band-nudge': true } });
    setBandPopupOpen(false);
    setBandsCardOpen(true);
  };

  // Pull-Up bar popup — one-time nudge for no-equipment PPL Pull Day users.
  // Fires contextually when the user is on Pull Day; gated by affiliates flag.
  // NOTE: showPullBarCondition is declared below, after isPaid (useTierState line).
  const [pullBarPopupOpen, setPullBarPopupOpen] = useState(false);
  const [pullBarCardOpen, setPullBarCardOpen] = useState(false);

  const dismissPullBarPopup = () => {
    update({ seenContextualPrompts: { ...seenPrompts, 'pullup-bar-nudge': 'dismissed' } });
    setPullBarPopupOpen(false);
  };
  const openPullBarFromPopup = () => {
    update({ seenContextualPrompts: { ...seenPrompts, 'pullup-bar-nudge': true } });
    setPullBarPopupOpen(false);
    setPullBarCardOpen(true);
  };
  const units = profile.units || 'imperial';
  const wUnit = weightUnit(units);

  const tierLimit = profile.minutesPerSession === 30 ? 1 : profile.minutesPerSession === 45 ? 2 : 3;
  // CI-9: session override for daily auto-regulation. `null` follows
  // the auto signal (capacity + pain); 'full' forces the complete session;
  // 'lighter' is a manual ease-back on a green-readiness day (preserves the CI-6
  // opt-in). The effective tier limit is derived from `regLevel` below, after
  // readiness is computed.
  const [sessionOverride, setSessionOverride] = useState(null);
  const flaggedAreas = useMemo(
    () => Object.entries(profile.injuries || {}).filter(([, v]) => v).map(([k]) => k),
    [profile.injuries]
  );

  // Resolve user's program + schedule
  const programType = profile.programType || (profile.noEquipment ? 'calisthenics' : 'upper-lower');
  const program = useMemo(() => getProgram(programType), [programType]);
  const hasRecovery = (profile.recoveryActivities || []).length > 0;
  // useTierState moved before userSchedule so isPaid is available to pass as
  // baselineOnly into getScheduleForWeek — needed for Bro Split 5/6/7-day rotation
  // selection (free users get 4-day template set + endurance fill; paid get standalone
  // muscle-group day templates). Declared here rather than at line ~289 where it was
  // previously; showPullBarCondition and openAiOrPaywall below still reference isPaid
  // correctly since they're declared after this line.
  const { is_paid: isPaid } = useTierState();
  const userSchedule = useMemo(() => {
    // Manual override always wins
    if (Array.isArray(profile.schedule) && profile.schedule.length === 7) return profile.schedule;
    // Otherwise generate from program + startDate (defaults to today if missing)
    return getScheduleForWeek({
      programId: programType,
      experience: profile.experience,
      daysPerWeek: profile.daysPerWeek || 4,
      startDate: profile.startDate || localISO(new Date()),
      hasRecovery,
      viewDate: new Date(),
      baselineOnly: !isPaid,
    });
  }, [profile.schedule, programType, profile.experience, profile.daysPerWeek, profile.startDate, hasRecovery, isPaid]);

  // Today's slot index (Mon=0..Sun=6) wrapped to schedule length
  const realTodayIdx = todayIdx();
  const [selectedDayIdx, setSelectedDayIdx] = useState(realTodayIdx % userSchedule.length);
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  const [showProgramPicker, setShowProgramPicker] = useState(false);
  const [showAiWorkout, setShowAiWorkout] = useState(false);
  const [aiPaywallOpen, setAiPaywallOpen] = useState(false);
  const showPullBarCondition = affiliatesEnabled && !isPaid && !!profile.noEquipment && !seenPrompts['pullup-bar-nudge'];
  const openAiOrPaywall = useCallback(() => {
    if (isPaid) setShowAiWorkout(true);
    else setAiPaywallOpen(true);
  }, [isPaid]);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [showEquipmentEdit, setShowEquipmentEdit] = useState(false);
  // Celebration modal after a workout completes if PRs / session milestones hit.
  // { prs: [...], sessionMilestone: number|null } or null = no celebration.
  const [celebration, setCelebration] = useState(null);
  // Program completion confirmation modal — fires when the user crosses the
  // program's target duration (default 12 weeks). Anti-shame: user confirms
  // or extends, app never silently auto-completes.
  const [programCompletionPrompt, setProgramCompletionPrompt] = useState(null);
  const [sharingMilestone, setSharingMilestone] = useState(null); // milestone content object
  // User-added blocks for today's workout (persisted in localStorage alongside sets)
  const [extraBlocks, setExtraBlocks] = useState([]);
  const [swappedExercises, setSwappedExercises] = useState({}); // { blockIdx: fullExObj } — user-swapped template slots
  const [replaceTarget, setReplaceTarget] = useState(null); // blockIdx being replaced | null
  // AI "use today" override: when set, an AI-generated session fully replaces the
  // scheduled plan as the loggable set-log until the user reverts. Shape:
  // { title, muscles: string[], blocks: [{ refId, ex, sets, reps, ... }] }.
  const [aiOverride, setAiOverride] = useState(null);
  // Today's pain + wellbeing are READ-ONLY on Strength (CI-5): both are logged
  // on the Dashboard/Progress page and surfaced here as info only. Reading
  // straight from the profile log keeps this in sync with edits made over there.
  const todayKey = localISO(new Date());
  const todayPainEntry = (profile.painLog || {})[todayKey];
  const todayPainLevel = todayPainEntry?.level ?? null;
  const todayWellbeingEntry = (profile.wellbeingLog || {})[todayKey];
  // Conservative pain banding + capacity ceiling (CI-9) applies to the
  // chronic-pain submode ONLY — a systemic condition warrants whole-body caution.
  // Amputee users get normal systemic readiness; their residual-limb signals will
  // drive a REGIONAL layer (Phase A), never a global deload. Mirrors backend
  // get_pacing.
  const conservative = (profile.adaptiveSubmodes || []).includes('chronic-pain');
  const isAmputee = (profile.adaptiveSubmodes || []).includes('amputee');

  // CI-9 Phase A: today's residual-limb log drives the regional deload layer.
  // Fetched only for amputee users; null until the fetch resolves (treated as
  // green = no regional deload while loading, which is the safe default).
  const [todayLimbLog, setTodayLimbLog] = useState(null);
  useEffect(() => {
    if (!isAmputee) return;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    fetch(`${API}/residual-limb-logs/${todayKey}`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data?.log) setTodayLimbLog(data.log); })
      .catch(() => { /* silent — no limb log = green regional, same as no data */ });
  }, [isAmputee, todayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Regional regulation: phantom pain + socket + skin → regional level.
  const regionalReg = useMemo(() => {
    if (!isAmputee || !todayLimbLog) return { level: 'green', reason: null, driver: null };
    return regionalRegulationLevel(
      todayLimbLog.phantom?.level,
      todayLimbLog.socket?.fit,
      todayLimbLog.skin?.status,
    );
  }, [isAmputee, todayLimbLog]);

  // User override for the regional layer (separate from systemic override).
  const [regionalOverride, setRegionalOverride] = useState(false); // true = run full affected exercises

  // Affected muscle groups for this user's specific prosthesis variants.
  const affectedMuscles = useMemo(
    () => getAffectedMuscleGroups(profile.prosthesisVariants),
    [profile.prosthesisVariants],
  );

  const painResult = useMemo(
    () => (todayPainLevel !== null ? painAdjustment(todayPainLevel, conservative) : null),
    [todayPainLevel, conservative],
  );
  // CI-5: daily readiness comes from the backend capacity engine (single source
  // of truth — same number the Dashboard PacingBanner shows). While /pacing is
  // loading or offline we fall back to a local mirror of the same math.
  const { capacity: pacingCapacity } = usePacing();
  const readinessFromBackend = !!(pacingCapacity
    && pacingCapacity.capacity_score !== null
    && pacingCapacity.capacity_score !== undefined);
  const readinessScore = useMemo(() => (
    readinessFromBackend
      ? pacingCapacity.capacity_score
      : localCapacityFallback(todayPainLevel, todayWellbeingEntry, conservative)
  ), [readinessFromBackend, pacingCapacity, todayPainLevel, todayWellbeingEntry, conservative]);
  const intensity = useMemo(() => {
    const base = readinessFromCapacity(readinessScore);
    // Prefer the backend's pain-aware reason copy when we have live data.
    if (readinessFromBackend && pacingCapacity.capacity_reason && readinessScore !== null) {
      return { ...base, message: pacingCapacity.capacity_reason };
    }
    return base;
  }, [readinessScore, readinessFromBackend, pacingCapacity]);

  // CI-9: closed-loop auto-regulation — readiness + today's pain scale volume.
  const autoReg = useMemo(
    () => autoRegulationLevel(readinessScore, todayPainLevel, conservative),
    [readinessScore, todayPainLevel, conservative],
  );
  // User override wins over the auto signal.
  const regLevel = sessionOverride === 'full'
    ? 'green'
    : sessionOverride === 'lighter'
      ? (autoReg.level === 'red' ? 'red' : 'amber')
      : autoReg.level;
  const effectiveTierLimit = regLevel === 'red'
    ? 1
    : regLevel === 'amber'
      ? Math.max(1, tierLimit - 1)
      : tierLimit;

  const [showIntensityBreakdown, setShowIntensityBreakdown] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState(userSchedule);
  const [copyConfirmed, setCopyConfirmed] = useState(false);

  // Historical logging — when set, overrides the current-week date math in
  // `selectedDate` and lets the user log a workout against any date in the
  // last 14 days. The day-picker grid covers the current week already; this
  // covers everything older. Picking a custom date also realigns
  // selectedDayIdx to the day-of-week of that date so the template loads.
  const [customLogDate, setCustomLogDate] = useState(null); // Date | null
  const [showHistoricalPicker, setShowHistoricalPicker] = useState(false);

  // Keep schedule draft in sync with userSchedule when it changes from outside
  useEffect(() => { setScheduleDraft(userSchedule); }, [userSchedule]);

  // Pain "log it" nudge — shown only when pain isn't logged yet today. Pain
  // editing lives on the Dashboard/Progress page (CI-5); this is an info prompt
  // with a link, not an inline editor.
  const showInlinePainBanner = !painResult && shouldShowPainCheck(profile, profile.painLog);

  // ----- Compute the calendar date for the currently-selected schedule day -----
  // Week is anchored Mon=0 .. Sun=6. selectedDayIdx maps to one of those slots.
  // We use Monday of the current calendar week + selectedDayIdx days.
  // If customLogDate is set (user picked a date outside this week via the
  // "Log a past workout" affordance), that date wins.
  const selectedDate = useMemo(() => {
    if (customLogDate) {
      const d = new Date(customLogDate);
      d.setHours(0, 0, 0, 0);
      return d;
    }
    const now = new Date();
    const monday = new Date(now);
    const dayOfWeek = (now.getDay() + 6) % 7; // 0=Mon, 6=Sun
    monday.setDate(now.getDate() - dayOfWeek);
    monday.setHours(0, 0, 0, 0);
    const d = new Date(monday);
    d.setDate(monday.getDate() + selectedDayIdx);
    return d;
  }, [selectedDayIdx, customLogDate]);

  const isLoggingHistorical = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate.getTime() < today.getTime();
  }, [selectedDate]);

  const isLoggingFuture = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate.getTime() > today.getTime();
  }, [selectedDate]);

  const daysAgo = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.floor((today.getTime() - selectedDate.getTime()) / 86400000);
  }, [selectedDate]);

  // ---------- Already-logged-today detection ----------
  // If the user has already logged a workout for the selected date, we don't
  // want to silently show them empty exercise cards (which look like a fresh
  // session in progress). Show a gate card listing what's logged + explicit
  // CTAs: "Log another" (rare but valid for split AM/PM sessions) or
  // "Edit logged workout" (open the existing edit modal).
  const sameDayWorkouts = useMemo(() => {
    const target = new Date(selectedDate);
    target.setHours(0, 0, 0, 0);
    const targetMs = target.getTime();
    return (history || []).filter((w) => {
      if (!w?.date) return false;
      const d = new Date(w.date);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === targetMs;
    });
  }, [history, selectedDate]);
  // User-toggled override — lets them explicitly bypass the gate to log a
  // second session for the same day. Resets when the selected date changes
  // (the gate becomes relevant again for a fresh day).
  const [logAnotherUnlocked, setLogAnotherUnlocked] = useState(false);
  useEffect(() => { setLogAnotherUnlocked(false); }, [selectedDate]);
  const [editingLoggedEntry, setEditingLoggedEntry] = useState(null);
  const dayAlreadyLogged = sameDayWorkouts.length > 0 && !logAnotherUnlocked;

  // Overtraining warnings
  const overtrainingWarnings = useMemo(
    () => detectOvertraining(history, profile),
    [history, profile]
  );

  // Build today's workout from selected schedule slot.
  // getTemplate uses a date-seeded shuffle so every calendar day draws a fresh random
  // selection from the eligible exercise pool. Same date = same exercises on reload
  // (deterministic seed), different date = different exercises (library variety).
  const todayTemplateKey = userSchedule[selectedDayIdx];
  const todayDateStr = localISO(new Date());
  // Universal equipment gate (Jul 2026): the single source of truth for "can the
  // user perform this movement with the gear they own?" Bodyweight (no equipment)
  // is always allowed; Full Gym owns everything; otherwise the user must own every
  // required piece. Used to build the scheduled plan (getTemplate), substitutions,
  // and dedup — so equipment is respected on every day of every program, not just
  // during injury swaps. (A regression restored here: base-plan equipment filtering
  // was lost in the pre-Cursor migration.)
  // "Bodyweight setup" = no gym equipment at all, OR the user only has lightweight
  // home gear (bands, pull-up bar, rings). Adding a $25 doorway bar should NOT eject
  // a no-equipment user into the full-gym PPL pool — they stay on the free BW templates
  // and Pull-Up / Chin-Up simply unlock within that template via skipEquipGate.
  const HOME_GEAR_IDS = new Set(['pullup-bar', 'bands', 'mini-band', 'resistance-bands', 'gymnastics-rings', 'parallette-bars', 'dip-bars']);
  const isBodyweightSetup = !!profile.noEquipment ||
    ((profile.equipment || []).length > 0 &&
     (profile.equipment || []).every((id) => HOME_GEAR_IDS.has(id)));

  const ownsEquipment = useCallback((x) => {
    if (!x || !Array.isArray(x.equipment) || x.equipment.length === 0) return true;
    if (profile.fullGym) return true;
    const owned = profile.equipment || [];
    return x.equipment.every((id) => owned.includes(id));
  }, [profile.fullGym, profile.equipment]);

  // Does this user train combatives? Signal is intentionally broad: the onboarding
  // "Train Combatives" equipment toggle OR any selected martial art. Combatives-
  // specific moves only schedule for these users, and their general strength work is
  // prioritized toward combatives-beneficial lifts. (Per-art precision is a follow-up.)
  const trainsCombatives = useMemo(() => {
    const ma = profile.martialArts;
    const hasArt = Array.isArray(ma) ? ma.length > 0 : !!ma && Object.keys(ma).length > 0;
    return hasArt || (Array.isArray(profile.equipment) && profile.equipment.includes('combatives'));
  }, [profile.martialArts, profile.equipment]);

  // Tier-gated library variety (founder spec Jul 2026): trial/paid draw from the FULL
  // library (wide variety); free tier (trial expired) draws only the baseline subset
  // — a visibly narrower scheduled plan that upgrading restores. `isPaid` is the gate
  // (once trial metering ships, "full access" = paid || in-trial).
  // No-equipment free-tier redirect (Jul 2026): Full Body, Upper/Lower, PPL, and Bro Split
  // templates are built for users with at least some equipment. When a free user has
  // noEquipment=true and selects one of those programs, the pinned exercises all fail the
  // equipment gate and fall through to a very sparse bodyweight pool. Rather than serve a
  // broken 2-exercise workout, silently redirect template generation to the Calisthenics
  // program — the only program currently designed for no-equipment free users. A
  // Option B (Jul 2026): each program now ships its own pinned bodyweight free-tier
  // templates. getTemplate handles the routing transparently when opts.noEquipment is
  // set — no redirect needed here. Strength page stays on the user's chosen program;
  // exercises are simply drawn from the no-equipment pool for that split.
  const todayTemplate = useMemo(
    () => getTemplate(programType, todayTemplateKey, todayDateStr, {
      ownsEquipment,
      trainsCombatives,
      baselineOnly: !isPaid,
      // Founder spec Jul 2026: exercise eligibility is experience-aware — a
      // beginner never gets handed an advanced-only movement. Every exercise's
      // `levels` array has existed since Day 1 for exactly this check.
      experience: profile.experience || null,
      // Full-gym users get band-only exercises deprioritized in the shuffle pool
      // (machine/cable alternatives win when available). Category B band moves with
      // no machine equivalent — Lateral Band Walk, Clamshell, etc. — still appear
      // when they're the only eligible option for a slot. See buildTemplate.
      fullGym: !!profile.fullGym,
      // Passed to getTemplate so the Option-B BW routing can engage when appropriate.
      noEquipment: isBodyweightSetup,
    }),
    [programType, todayTemplateKey, todayDateStr, ownsEquipment, trainsCombatives, isPaid, profile.experience, profile.fullGym, profile.noEquipment, profile.equipment, isBodyweightSetup]
  );
  const isRecoveryDay = todayTemplateKey === 'recovery';
  // Bro Split 6-day: the 6th slot is a scheduled conditioning/endurance day.
  // Strength page has no template for it — renders a redirect card to the Endurance tab.
  const isEnduranceDay = todayTemplateKey === 'endurance';
  // PPL+C and Bro Split don't have bodyweight-only templates (Aug 2026 — research does
  // not support single-muscle-group days for no-equipment or band-only users).
  // Show a redirect card pointing to Calisthenics, Full Body, or Upper/Lower instead.
  const isBodyweightRedirect = isBodyweightSetup && (programType === 'ppl' || programType === 'bro');
  const isRestDay = todayTemplateKey === 'rest' || (!todayTemplate && !isRecoveryDay && !isEnduranceDay && !isBodyweightRedirect);

  // Trigger pull-up bar nudge when user navigates to PPL Pull Day.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (showPullBarCondition && programType === 'ppl' && todayTemplateKey === 'pull') {
      setPullBarPopupOpen(true);
    }
  }, [todayTemplateKey, programType]);

  // Circuit mode: active when the current program has `circuit: true` (Calisthenics).
  // circuitRounds = how many times all exercises are looped through this session
  // (varies by session length: 30min=2, 45min=3, 60min=3).
  const currentProgramDef = getProgram(programType);
  const isCircuit = !!currentProgramDef?.circuit;
  const circuitRoundsMap = currentProgramDef?.circuitRounds || {};
  const circuitRounds = isCircuit ? (circuitRoundsMap[tierLimit] ?? 3) : null;

  // Map the scheduled day's title to an AI day-type slug so the "Ignite a custom
  // day" picker opens pre-selected to what's on the calendar (one-tap common case).
  // Recovery/rest days seed to Stretching; anything unrecognized falls to Full Body.
  const defaultDayType = useMemo(() => {
    if (isRecoveryDay || isRestDay || isEnduranceDay || isBodyweightRedirect) return 'stretching';
    const t = (todayTemplate?.title || '').toLowerCase();
    if (t.includes('push')) return 'push';
    if (t.includes('pull')) return 'pull';
    if (t.includes('upper')) return 'upper';
    if (t.includes('lower')) return 'lower';
    if (t.includes('leg') || t.includes('quad') || t.includes('hamstring') || t.includes('glute')) return 'legs';
    if (t.includes('arm') || t.includes('bicep') || t.includes('tricep')) return 'arms';
    if (t.includes('shoulder') || t.includes('delt')) return 'shoulders';
    if (t.includes('core') || t.includes('ab')) return 'core';
    if (t.includes('stretch') || t.includes('mobility') || t.includes('recovery')) return 'stretching';
    return 'full-body';
  }, [todayTemplate, isRecoveryDay, isRestDay, isEnduranceDay, isBodyweightRedirect]);

  // Build plan blocks with tier/contraindication substitution. Track exercises already in the plan
  // so a substitution can't accidentally produce a duplicate exercise within the same day.
  const planBuild = useMemo(() => {
    if (!todayTemplate) return { blocks: [], injuryRemoved: [] };
    const planUsed = new Set();
    // Exercises that leave the session entirely because a flagged area has no safe,
    // non-duplicate alternative. Captured (not silently dropped) so "Today's
    // Adjustments" can surface the loss and Phase 2 can offer per-item reclaim.
    const injuryRemoved = [];

    // A2b: the substitution/dedup pool is tier-gated. The single fork is `paid?`.
    // Paid users draw from the ENTIRE library; free (Egnite) users draw only from
    // their 59-move baseline subset — so a flagged free user never silently
    // receives a paid-locked substitute. (The baseline spans strength + stretch +
    // mobility/rehab, so safe subs still exist within it.)
    const libraryPool = isPaid ? exercises : exercises.filter(isBaselineExercise);
    // Injury-first pass: build a safe pool once so every substitution and
    // dedup candidate draw from the same injury-filtered set. When there are
    // no flagged areas, skip the filter entirely to keep the common case fast.
    const safePool = flaggedAreas.length > 0
      ? libraryPool.filter((x) => !x.contraindications.some((c) => flaggedAreas.includes(c)))
      : libraryPool;

    // Movement-type guards (RC-1/RC-2, Jul 2026): a STRENGTH slot is replaced by
    // a strength move — NEVER a stretch — and only steps down to a rehab/mobility
    // drill when the flag is paired with real reported pain (moderate/severe
    // today). A flag alone lands on a lighter strength move, or drops (surfaced),
    // rather than assuming the user needs rehab. Equipment is always respected —
    // we never hand back a move the user can't perform.
    const isStretchMove = (x) => Array.isArray(x.tags) && x.tags.includes('stretching');
    const isRehabMove = (x) => !!x.ptClinical || (Array.isArray(x.tags) && x.tags.includes('rehab'));
    const painGatesRehab = todayPainLevel !== null
      && painAdjustment(todayPainLevel, conservative).tier !== 'green';
    const chooseSub = (cands, tier) => {
      const pickOwned = (pool) => {
        const owned = pool.filter(ownsEquipment);
        return owned.find((x) => x.tier === tier) || owned[0] || null;
      };
      const strength = cands.filter((x) => !isStretchMove(x) && !isRehabMove(x));
      const rehab = cands.filter((x) => !isStretchMove(x) && isRehabMove(x));
      // No/low pain → keep training: a lighter STRENGTH move; rehab excluded so a
      // flag alone never assumes the user needs rehab. Moderate/severe pain (+flag)
      // → lead with the gentler rehab/mobility move, then fall back to lighter
      // strength so the slot isn't fully gutted. Never a stretch on a strength slot.
      return painGatesRehab
        ? (pickOwned(rehab) || pickOwned(strength))
        : pickOwned(strength);
    };

    const blocks = todayTemplate.blocks.map((b) => {
      const ex = getExerciseById(b.refId);
      if (!ex) return null;
      const conflicts = ex.contraindications.some((c) => flaggedAreas.includes(c));
      let finalEx = ex;
      let substituted = false;
      let beneficialPick = false;

      if (conflicts) {
        const candidates = safePool.filter((x) =>
          x.muscle === ex.muscle &&
          x.id !== ex.id &&
          !planUsed.has(x.id) &&
          (!profile.experience || x.levels.includes(profile.experience))
        );
        const sub = chooseSub(candidates, ex.tier);
        if (sub) { finalEx = sub; substituted = true; beneficialPick = isBeneficialExercise(sub); }
        else {
          // No safe strength sub, and pain doesn't justify a rehab step-down:
          // drop the flagged move and surface it in Today's Adjustments.
          injuryRemoved.push({
            name: ex.name,
            muscle: ex.muscle,
            areas: ex.contraindications.filter((c) => flaggedAreas.includes(c)),
          });
          return null;
        }
      }

      // Dedup: replace a repeated slot with another same-muscle strength move.
      if (planUsed.has(finalEx.id)) {
        const altCandidates = safePool.filter((x) =>
          x.muscle === finalEx.muscle &&
          !planUsed.has(x.id) &&
          (!profile.experience || x.levels.includes(profile.experience))
        );
        const alt = chooseSub(altCandidates, finalEx.tier);
        if (alt) { finalEx = alt; substituted = true; }
        else {
          // No safe, non-duplicate alternative. If this slot was flagged-driven,
          // record the loss for the transparency list; a pure template duplicate
          // (not injury-related) is just a redundant slot, so don't surface it.
          if (conflicts) {
            injuryRemoved.push({
              name: ex.name,
              muscle: ex.muscle,
              areas: ex.contraindications.filter((c) => flaggedAreas.includes(c)),
            });
          }
          return null;
        }
      }

      planUsed.add(finalEx.id);
      const presc = getPrescription(finalEx.tier, profile.experience || 'intermediate');
      return {
        ...b,
        ex: finalEx,
        substituted,
        beneficialPick,
        originalName: ex.name,
        reps: b.reps || presc.reps,
      };
    }).filter(Boolean);
    return { blocks, injuryRemoved };
  }, [todayTemplate, flaggedAreas, profile.experience, profile.equipment, profile.fullGym, isPaid, todayPainLevel, conservative, ownsEquipment]);

  const planRaw = planBuild.blocks;
  const injuryRemoved = planBuild.injuryRemoved;

  // Computed here (not inside a memo) so both `plan` and `todaysAdjustments` can read it.
  const hasPullupBar = (profile.equipment || []).includes('pullup-bar');

  const plan = useMemo(() => {
    // An applied AI session fully replaces the scheduled day (user's explicit
    // choice) — it becomes the entire loggable session until reverted.
    if (aiOverride && Array.isArray(aiOverride.blocks) && aiOverride.blocks.length) {
      return aiOverride.blocks;
    }
    // Gate on `phase` when a slot sets one (Full Body's breadth-vs-depth
    // duration gating — see programs.js), falling back to the exercise's own
    // tier for every other program, unchanged.
    const base = planRaw.filter((b) => b.ex && (b.phase ?? b.ex.tier) <= effectiveTierLimit);

    // Free-pull volume compensation — Pull-Up (32) and Chin-Up (33) are intermediate+
    // movements that only appear with a bar. When absent (no bar, or beginner), the missing
    // sets create a time shortfall:
    //   45min: PU missing = 3 lost sets → 15 sets = 37.5 min (target: 45 min)
    //   60min: PU+CU missing = 6 lost sets → 18 sets = 45 min (target: 60 min)
    // Compensate by distributing the missing sets across accessible compound anchors.
    const userCantDoPullUps = !hasPullupBar || profile.experience === 'beginner';
    // IDs boosted at 45min: the 3 horizontal-pull anchors (+1 set each = +3 sets = 18 total = 45 min)
    const BOOST_IDS_45 = new Set([372, 393, 373]);
    let volumeAdjusted;
    if (isBodyweightSetup && todayTemplateKey === 'pull' && userCantDoPullUps) {
      if (effectiveTierLimit >= 3) {
        // 60min: 6 accessible × 4 sets × 2.5 = 60 min ✓
        volumeAdjusted = base.map((b) => (b.ex?.id !== 32 && b.ex?.id !== 33) ? { ...b, sets: 4 } : b);
      } else if (effectiveTierLimit >= 2) {
        // 45min: 3 compound anchors → 4 sets, rest stay at 3 → 3×4+2×3 = 18 sets × 2.5 = 45 min ✓
        volumeAdjusted = base.map((b) => BOOST_IDS_45.has(b.ex?.id) ? { ...b, sets: 4 } : b);
      } else {
        // 30min: 4 exercises × 3 sets × 2.5 = 30 min ✓ — template sets are exact
        volumeAdjusted = base;
      }
    } else {
      volumeAdjusted = base;
    }

    // Pull-Up and Chin-Up lead the session — hardest compound first.
    // No-op when neither is in the plan (30min, or beginner where eligible() filtered them out).
    const PU_ORDER = { 32: 0, 33: 1 };
    const puSorted = (isBodyweightSetup && todayTemplateKey === 'pull')
      ? [...volumeAdjusted].sort((a, b) => (PU_ORDER[a.ex?.id] ?? 2) - (PU_ORDER[b.ex?.id] ?? 2))
      : volumeAdjusted;

    // Muscle-group ordering (Aug 2026 — founder spec):
    // The session-length gating system appends exercises in slot-declaration order.
    // On Pull and Legs days, 45min/60min additions interleave with 30min muscles
    // because they are appended by phase, not grouped by muscle.
    // Fix: stable-sort Pull and Legs days by muscle group priority.
    // Scoped to 'pull' and 'legs' only — Full Body/Upper-Lower already declare slots
    // in correct muscle-group order; a global sort would break those sessions.
    // Key: sort on a.ex.muscle (exercise's own field from exercises.js).
    const PULL_MUSCLE_PRIORITY = { Back: 0, Shoulders: 1, Arms: 2 };
    const LEGS_MUSCLE_PRIORITY = { Quads: 0, Hamstrings: 1, Glutes: 2, Calves: 3 };
    const ordered = (todayTemplateKey === 'pull')
      ? [...puSorted].sort((a, b) =>
          (PULL_MUSCLE_PRIORITY[a.ex?.muscle] ?? 50) - (PULL_MUSCLE_PRIORITY[b.ex?.muscle] ?? 50)
        )
      : (todayTemplateKey === 'legs')
        ? [...puSorted].sort((a, b) =>
            (LEGS_MUSCLE_PRIORITY[a.ex?.muscle] ?? 50) - (LEGS_MUSCLE_PRIORITY[b.ex?.muscle] ?? 50)
          )
        : puSorted;

    return [...ordered, ...extraBlocks];
  }, [aiOverride, planRaw, effectiveTierLimit, extraBlocks, isBodyweightSetup, hasPullupBar, todayTemplateKey, profile.experience]);

  // "Today's Adjustments" (P1, read-only): aggregate every engine that held,
  // removed, swapped, or eased part of today's SCHEDULED session into one list for
  // the disclosure banner. Skipped when an AI session is applied (its own
  // transparency lives in the modal) or on rest/recovery days (no plan to adjust).
  // All inputs are already in memory — no re-plumbing. Copy is anti-shame: protective
  // framing, points at the existing restore controls, never "you can't".
  const todaysAdjustments = useMemo(() => {
    if (aiOverride || isRestDay || isRecoveryDay || isEnduranceDay || isBodyweightRedirect) return { groups: [], count: 0 };
    const groups = [];

    // 1. Held — only surface when readiness/pain shortened the session, not when the
    //    user chose a shorter duration. They picked 30 min; they don't need to know
    //    what they'd do with 60 min. The coach knows; surface it only when their body
    //    asked for it, not when their calendar did.
    const held = planRaw.filter((b) => b.ex && (b.phase ?? b.ex.tier) > effectiveTierLimit);
    if (held.length) {
      const byReadiness = effectiveTierLimit < tierLimit; // reg level shrank the cap below their chosen limit
      if (byReadiness) {
        groups.push({
          key: 'held',
          title: 'Held while you recover',
          reason: "Higher-effort moves are paused today based on your check-in. Restore the full session in the notice above to bring them back.",
          items: held.map((b) => ({ name: b.ex.name, detail: '' })),
        });
      }
    }

    // 2. Set aside — flagged area with no safe alternative.
    if (injuryRemoved.length) {
      groups.push({
        key: 'removed',
        title: 'Set aside for your flagged areas',
        reason: "No joint-friendly alternative fit today, so these sat out to protect the areas you flagged.",
        items: injuryRemoved.map((r) => ({ name: r.name, detail: (r.areas || []).join(', ') || 'flagged area' })),
      });
    }

    // 3. Swapped — flagged/joint-friendly substitution that trains the same muscle.
    const swapped = plan.filter((b) => b.substituted && b.originalName && b.ex && b.originalName !== b.ex.name);
    if (swapped.length) {
      groups.push({
        key: 'swapped',
        title: 'Swapped for a safer fit',
        reason: "These were exchanged for moves that train the same muscle without loading a flagged area.",
        items: swapped.map((b) => ({ name: `${b.originalName} → ${b.ex.name}`, detail: b.beneficialPick ? 'joint-friendly' : 'flagged-area sub' })),
      });
    }

    // 4. Lighter volume — systemic ease from readiness/pain or a manual lighter day.
    if (regLevel === 'amber' || regLevel === 'red') {
      const eased = plan.filter((b) => b.ex && (regLevel === 'red' || b.ex.tier > 1));
      if (eased.length) {
        groups.push({
          key: 'eased',
          title: 'Lighter volume today',
          reason: sessionOverride === 'lighter'
            ? "You chose a lighter day — we trimmed a set from the accessory work."
            : "We trimmed some sets to keep today steady, based on your check-in. Restore the full session in the notice above for full volume.",
          items: [{ name: `${eased.length} move${eased.length > 1 ? 's' : ''} with fewer sets`, detail: regLevel === 'red' ? 'recovery-level' : 'eased' }],
        });
      }
    }

    // 5. Prosthesis-loaded work eased — amputee regional layer.
    if (isAmputee && !regionalOverride && regionalReg.level !== 'green') {
      const regional = plan.filter((b) => b.ex && isExerciseRegionallyAffected(b.ex, affectedMuscles));
      if (regional.length) {
        groups.push({
          key: 'regional',
          title: 'Prosthesis-loaded work eased',
          reason: regionalReg.reason || "Residual-limb-loaded moves were eased today. Restore full volume in the notice above to bring them back.",
          items: regional.map((b) => ({ name: b.ex.name, detail: 'residual-limb load' })),
        });
      }
    }

    // 6. No pull-up bar — Pull-Up / Chin-Up are in the session via skipEquipGate so the
    //    user sees the programmed movements, but they need a bar to perform them.
    //    Show a substitution note pointing to Floor Lat Pulldown (already in 30min session).
    const pullBarExInPlan = plan.some((b) => b.ex?.id === 32 || b.ex?.id === 33);
    if (programType === 'ppl' && todayTemplateKey === 'pull' && !hasPullupBar && pullBarExInPlan) {
      groups.push({
        key: 'pullbar',
        title: 'No pull-up bar yet',
        reason: 'Pull-Up and Chin-Up are programmed in this session. Until you have a bar, Floor Lat Pulldown (in your session) is your vertical-pull substitute — same lat pattern, no bar needed. Pull-up bar options are in the Recommendations section below.',
        items: [
          { name: 'Pull-Up → Floor Lat Pulldown', detail: 'substitute' },
          { name: 'Chin-Up → Floor Lat Pulldown', detail: 'substitute' },
        ],
      });
    }

    const count = groups.reduce((n, g) => n + g.items.length, 0);
    return { groups, count };
  }, [aiOverride, isRestDay, isRecoveryDay, isEnduranceDay, isBodyweightRedirect, planRaw, injuryRemoved, effectiveTierLimit, tierLimit, plan, regLevel, sessionOverride, isAmputee, regionalOverride, regionalReg, affectedMuscles, programType, todayTemplateKey, profile.equipment]);

  // Unified handler for both "add to bottom" and "replace in place" exercise selection.
  // When replaceTarget is null → append as extra block.
  // When replaceTarget is a block index → swap that slot's exercise via swappedExercises.
  const handleExerciseSelected = (entry) => {
    const fullEx = !entry.isCustom && entry.id ? getExerciseById(entry.id) : null;
    const refId = entry.isCustom ? `custom:${entry.id}` : entry.id;
    const exObj = fullEx || {
      id: refId,
      name: entry.name,
      muscle: entry.muscle || '—',
      primaryMuscles: entry.primaryMuscles || [],
      secondaryMuscles: entry.secondaryMuscles || [],
      difficulty: entry.difficulty || 'Custom',
      contraindications: entry.contraindications || [],
      equipment: entry.equipment || [],
    };

    if (replaceTarget !== null) {
      setSwappedExercises((prev) => ({ ...prev, [replaceTarget]: exObj }));
      setReplaceTarget(null);
    } else {
      setExtraBlocks((arr) => [
        ...arr,
        {
          refId,
          ex: exObj,
          sets: entry.sets || '3x8-12',
          userAdded: true,
          custom: !!entry.isCustom,
          substituted: false,
          originalName: null,
        },
      ]);
    }
  };


  // Per-set state keyed by block index -> array of set objects
  // Persisted to localStorage so navigating to /exercise/:id and back doesn't wipe progress.
  // Key includes today's date + day index + program type so the storage rolls over each day.
  // Include a short user-scoped suffix so two different accounts on the same
  // device on the same calendar day never share in-progress state. user?.id
  // is the Auth0/backend user ID — always present for a logged-in session.
  // Fallback '_anon' keeps behaviour identical for unauthenticated edge cases.
  const inProgressKey = useMemo(
    () => `enferno_strength_inprogress_v2_${user?.id ?? 'anon'}_${localISO(new Date())}_${programType}_${selectedDayIdx}`,
    [user?.id, programType, selectedDayIdx],
  );

  // Persist extra (user-added) blocks in localStorage so they survive nav.
  useEffect(() => {
    try {
      const key = `${inProgressKey}_extras`;
      const raw = localStorage.getItem(key);
      if (raw) setExtraBlocks(JSON.parse(raw));
      else setExtraBlocks([]);
    } catch { setExtraBlocks([]); }
  }, [inProgressKey]);

  useEffect(() => {
    try {
      localStorage.setItem(`${inProgressKey}_extras`, JSON.stringify(extraBlocks));
    } catch { /* ignore */ }
  }, [extraBlocks, inProgressKey]);

  // Persist user-swapped exercises (replace-in-place) across nav, same key pattern as extras.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`${inProgressKey}_swaps`);
      if (raw) setSwappedExercises(JSON.parse(raw));
      else setSwappedExercises({});
    } catch { setSwappedExercises({}); }
  }, [inProgressKey]);
  useEffect(() => {
    try {
      localStorage.setItem(`${inProgressKey}_swaps`, JSON.stringify(swappedExercises));
    } catch { /* ignore */ }
  }, [swappedExercises, inProgressKey]);

  // Rehydrate / persist the AI-session override alongside the day's checkpoint so
  // navigating away mid-session (e.g. to watch an exercise video) doesn't drop it.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`${inProgressKey}_aiplan`);
      setAiOverride(raw ? JSON.parse(raw) : null);
    } catch { setAiOverride(null); }
  }, [inProgressKey]);

  useEffect(() => {
    try {
      if (aiOverride) localStorage.setItem(`${inProgressKey}_aiplan`, JSON.stringify(aiOverride));
      else localStorage.removeItem(`${inProgressKey}_aiplan`);
    } catch { /* ignore */ }
  }, [aiOverride, inProgressKey]);

  const [setsByBlock, setSetsByBlock] = useState(() => {
    try {
      const raw = localStorage.getItem(inProgressKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  // Per-exercise notes — { blockIdx: string }. Persisted alongside setsByBlock
  // under a derived key so existing in-progress workouts aren't disturbed.
  // Use case: "2x warmup sets first" tied to a specific lift; surfaces against
  // that exercise on future training days.
  const exerciseNotesKey = `${inProgressKey}.exerciseNotes`;
  const [notesByBlock, setNotesByBlock] = useState(() => {
    try {
      const raw = localStorage.getItem(exerciseNotesKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  // Per-workout note — single free-text field for whole-session commentary
  // ("not really feeling it today", "small twinge in shoulder", etc).
  const workoutNoteKey = `${inProgressKey}.workoutNote`;
  const [workoutNote, setWorkoutNote] = useState(() => {
    try {
      return localStorage.getItem(workoutNoteKey) || '';
    } catch {
      return '';
    }
  });
  const [workoutNoteOpen, setWorkoutNoteOpen] = useState(false);
  const [expandedBlock, setExpandedBlock] = useState(0); // index of expanded exercise
  // Circuit mode: which round the user is currently on (1-indexed). Resets to 1
  // when the plan changes (new day / program switch) via the useEffect below.
  const [currentCircuitRound, setCurrentCircuitRound] = useState(1);
  // Set of block indices the user marked "skip" for this session. Strikethrough
  // visual, no sets editable, excluded from totals + completion math. State
  // is per-session (cleared on day/program switch alongside sets/notes) —
  // intentional, because a skip is "I didn't do this today," not "I never
  // want to see this exercise again." Permanent removal is a Profile concern.
  const [skippedBlocks, setSkippedBlocks] = useState(() => new Set());
  const [autoStartRest, setAutoStartRest] = useState(true);
  const [previewEx, setPreviewEx] = useState(null);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [restSeconds, setRestSeconds] = useState(0);
  const [restRunning, setRestRunning] = useState(false);
  const [restTarget, setRestTarget] = useState(0);
  const timerRef = useRef(null);

  // Re-hydrate when the in-progress key changes (e.g. user swaps day or program)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(inProgressKey);
      setSetsByBlock(raw ? JSON.parse(raw) : {});
    } catch {
      setSetsByBlock({});
    }
    // Rehydrate notes alongside sets so day/program switches stay coherent.
    try {
      const rawNotes = localStorage.getItem(exerciseNotesKey);
      setNotesByBlock(rawNotes ? JSON.parse(rawNotes) : {});
    } catch {
      setNotesByBlock({});
    }
    // Skips and circuit round are per-session and reset whenever sets reset.
    setSkippedBlocks(new Set());
    setCurrentCircuitRound(1);
    try {
      setWorkoutNote(localStorage.getItem(workoutNoteKey) || '');
    } catch {
      setWorkoutNote('');
    }
  }, [inProgressKey, exerciseNotesKey, workoutNoteKey]);

  // Persist setsByBlock to localStorage on every change so navigation away
  // (e.g. to /exercise/:id to watch a video) doesn't wipe in-progress work.
  useEffect(() => {
    try {
      if (Object.keys(setsByBlock).length === 0) {
        localStorage.removeItem(inProgressKey);
      } else {
        localStorage.setItem(inProgressKey, JSON.stringify(setsByBlock));
      }
    } catch { /* quota / private mode — ignore */ }
  }, [setsByBlock, inProgressKey]);

  // AI "use today": the user generated a custom workout and chose it over the
  // scheduled day. Map the AI blocks into the same block shape planRaw produces
  // so the existing SetRow log + completeWorkout path just works, then start the
  // set-log clean (new block indices don't line up with the old session).
  const handleApplyAiWorkout = (workout, source = 'ai', extra = {}) => {
    const blocks = (workout?.blocks || [])
      .map((b) => {
        const ex = getExerciseById(b.ref_id) || getExerciseById(Number(b.ref_id));
        if (!ex) return null; // skip anything unresolvable (e.g. a custom-only ref)
        return {
          refId: ex.id,
          ex,
          sets: Number(b.sets) || 3,
          reps: b.reps || '',
          rest: b.rest_seconds,
          cue: b.cue,
          aiGenerated: source === 'ai',
          substituted: false,
          originalName: null,
        };
      })
      .filter(Boolean);
    if (!blocks.length) return;
    const muscles = [...new Set(blocks.map((b) => b.ex.muscle).filter(Boolean))];
    setSetsByBlock({});
    setNotesByBlock({});
    setWorkoutNote('');
    setAiOverride({ title: workout.title || 'AI Session', muscles, blocks, source, ...extra });
  };

  // Rest-day stretch routine — ONE fixed, curated head-to-toe set (neck -> ankle),
  // the same every time. It's the free sampler that showcases the stretch library
  // without unlocking AI variety. The single fork is `paid?`:
  //   • paid + flagged move  -> SUBSTITUTE with a safe stretch from the full
  //     library (VIP treatment — same as their other routines; full-length).
  //   • free + flagged move  -> DROP it (a swap would leak paid-locked moves),
  //     disclose via Today's Adjustments styling, offer one-tap restore-to-full.
  // The 13 sampler IDs are all in the free baseline subset (see BASELINE_EXERCISE_IDS).
  const handleStartRestStretch = (includeFlagged = false) => {
    const ROUTINE_IDS = [
      343, // Neck — Scalene (Side-Neck) Stretch
      308, // Shoulders — Cross-Body Shoulder Stretch
      311, // Biceps — Wall Biceps Stretch
      310, // Triceps — Overhead Triceps Stretch
      304, // Chest — Doorway Chest Stretch
      330, // Back/Lats — Standing Overhead Lat Stretch
      307, // Spine — Seated Spinal Twist
      320, // Core — Standing Side-Bend Stretch
      315, // Glutes — Figure-4 Glute Stretch
      312, // Quads — Standing Quad Stretch
      313, // Hamstrings — Standing Hamstring Stretch
      322, // Adductors — Butterfly Stretch
      317, // Calves — Wall Calf Stretch (Gastrocnemius)
    ];
    const routine = ROUTINE_IDS.map((id) => getExerciseById(id)).filter(Boolean);
    const stretchPool = exercises.filter((x) => Array.isArray(x.tags) && x.tags.includes('stretching'));
    const isConflict = (e) => Array.isArray(e.contraindications)
      && e.contraindications.some((c) => flaggedAreas.includes(c));
    // Keep a swap in the same body region — never trade a quad stretch for a
    // second chest stretch. If no same-region safe stretch exists, drop it
    // (surfaced + restorable) rather than padding an unrelated area.
    const LOWER = ['Legs', 'Hamstrings', 'Glutes', 'Calves', 'Adductors', 'Hip Flexors', 'Quads'];
    const CORE = ['Core', 'Abs'];
    const regionOf = (m) => LOWER.includes(m) ? 'lower' : CORE.includes(m) ? 'core' : 'upper';

    const usedIds = new Set();
    const dropped = [];
    const swapped = [];
    const finalEx = [];

    for (const e of routine) {
      if (includeFlagged || !flaggedAreas.length || !isConflict(e)) {
        finalEx.push(e); usedIds.add(e.id); continue;
      }
      if (isPaid) {
        // VIP: substitute a safe stretch — same muscle first, then same region —
        // so the routine stays full-length without wandering to another area.
        const region = regionOf(e.muscle);
        const sub = stretchPool.find((x) => x.muscle === e.muscle && !isConflict(x) && !usedIds.has(x.id) && x.id !== e.id)
          || stretchPool.find((x) => regionOf(x.muscle) === region && !isConflict(x) && !usedIds.has(x.id) && !ROUTINE_IDS.includes(x.id));
        if (sub) { finalEx.push(sub); usedIds.add(sub.id); swapped.push({ from: e.name, to: sub.name }); }
        else { dropped.push({ name: e.name, areas: e.contraindications.filter((c) => flaggedAreas.includes(c)) }); }
      } else {
        // Free: drop (surfaced + restorable below).
        dropped.push({ name: e.name, areas: e.contraindications.filter((c) => flaggedAreas.includes(c)) });
      }
    }

    if (!finalEx.length) {
      toast.error('Stretch routine unavailable right now — try Browse stretches.');
      return;
    }
    handleApplyAiWorkout({
      title: 'Rest-Day Stretch',
      blocks: finalEx.map((e) => ({
        ref_id: e.id,
        sets: 2,
        reps: '30s hold',
        rest_seconds: 20,
        cue: (typeof e.tips === 'string' ? e.tips.split('.')[0] : '') || 'Ease in, breathe, no bouncing.',
      })),
    }, 'static-stretch', { dropped, swapped });
    const eased = dropped.length + swapped.length;
    toast.success(eased
      ? `Stretch routine loaded — ${eased} move${eased > 1 ? 's' : ''} adjusted for your flagged areas.`
      : 'Stretch routine loaded — hold each, breathe, no rush.');
  };

  const handleRevertAiWorkout = () => {
    setAiOverride(null);
    setSetsByBlock({});
    setNotesByBlock({});
    setWorkoutNote('');
    try { localStorage.removeItem(`${inProgressKey}_aiplan`); } catch { /* ignore */ }
  };

  // Persist notes alongside sets so they survive nav-away mid-workout.
  useEffect(() => {
    try {
      const filled = Object.entries(notesByBlock).filter(([, v]) => (v || '').trim());
      if (filled.length === 0) {
        localStorage.removeItem(exerciseNotesKey);
      } else {
        localStorage.setItem(exerciseNotesKey, JSON.stringify(Object.fromEntries(filled)));
      }
    } catch { /* ignore */ }
  }, [notesByBlock, exerciseNotesKey]);

  useEffect(() => {
    try {
      if (!workoutNote.trim()) {
        localStorage.removeItem(workoutNoteKey);
      } else {
        localStorage.setItem(workoutNoteKey, workoutNote);
      }
    } catch { /* ignore */ }
  }, [workoutNote, workoutNoteKey]);

  // CI-9 Phase A: per-block effective regulation level — compounds systemic +
  // regional for exercises that physically load through the residual limb.
  // Non-affected exercises and non-amputee users always use the systemic regLevel.
  const effectiveRegLevelFor = useCallback((ex) => {
    if (!isAmputee || regionalOverride || regionalReg.level === 'green') return regLevel;
    if (!isExerciseRegionallyAffected(ex, affectedMuscles)) return regLevel;
    return compoundRegLevel(regLevel, regionalReg.level);
  }, [isAmputee, regionalOverride, regionalReg.level, regLevel, affectedMuscles]);

  // Initialize setsByBlock when plan changes (only for blocks not yet initialized)
  useEffect(() => {
    setSetsByBlock((prev) => {
      const next = { ...prev };
      let changed = false;
      plan.forEach((b, i) => {
        const effLevel = effectiveRegLevelFor(b.ex);
        const rows = next[i];
        // Circuit mode: each "set" represents one round through the full exercise list.
        // Use circuitRounds as the hard set count — bodyweight, no weight tracking.
        const makeCircuitDefaults = (rounds) => Array.from({ length: rounds }, () => ({
          id: newId(), parentId: null, type: 'normal', weight: '', reps: '', completed: false,
        }));
        if (!rows) {
          next[i] = isCircuit
            ? makeCircuitDefaults(circuitRounds)
            : makeDefaultSets(b, b.ex, profile.experience, history, regLevel, effLevel);
          changed = true;
          return;
        }
        // CI-9: reactively re-fit the set count when auto-regulation changes —
        // but ONLY for blocks the user hasn't started (no completed set, no reps
        // entered). Never disturb sets that already hold logged work.
        const untouched = rows.every((r) => !r.completed && (r.reps === '' || r.reps === null || r.reps === undefined));
        if (untouched) {
          const target = isCircuit
            ? makeCircuitDefaults(circuitRounds)
            : makeDefaultSets(b, b.ex, profile.experience, history, regLevel, effLevel);
          if (target.length !== rows.length) {
            next[i] = target;
            changed = true;
          }
        }
      });
      // Drop any indices no longer in plan
      Object.keys(next).forEach((k) => {
        if (Number(k) >= plan.length) { delete next[k]; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [plan, profile.experience, history, regLevel, effectiveRegLevelFor, isCircuit, circuitRounds]);

  // Rest timer
  useEffect(() => {
    if (restRunning && restSeconds > 0) {
      timerRef.current = setTimeout(() => setRestSeconds((s) => s - 1), 1000);
    } else if (restSeconds === 0) {
      setRestRunning(false);
    }
    return () => clearTimeout(timerRef.current);
  }, [restRunning, restSeconds]);

  const startRest = useCallback((sec) => {
    setRestTarget(sec);
    setRestSeconds(sec);
    setRestRunning(true);
  }, []);

  // ----- Set actions -----

  const updateSet = (blockIdx, setId, patch) => {
    setSetsByBlock((prev) => ({
      ...prev,
      [blockIdx]: prev[blockIdx].map((s) => (s.id === setId ? { ...s, ...patch } : s)),
    }));
  };

  const markSetDone = (blockIdx, setId) => {
    const block = plan[blockIdx];
    const beforeBlock = setsByBlock[blockIdx] || [];
    const targetBefore = beforeBlock.find((s) => s.id === setId);
    const willComplete = targetBefore && !targetBefore.completed;
    setSetsByBlock((prev) => {
      const cur = prev[blockIdx];
      const target = cur.find((s) => s.id === setId);
      if (!target) return prev;
      const nowCompleted = !target.completed;
      const updated = cur.map((s) => (s.id === setId ? { ...s, completed: nowCompleted } : s));
      return { ...prev, [blockIdx]: updated };
    });
    // Auto-start rest after marking complete.
    // Circuit mode skips per-exercise rest — the between-round rest fires only
    // when the user taps "Begin Round X+1" in the circuit header.
    if (willComplete && autoStartRest && !isCircuit) {
      // Rest is a flat, user-overridable default (founder spec Jul 2026) — no
      // longer tier/experience-driven. A per-block override (e.g. an AI-built
      // session) still wins when present.
      startRest(block.rest || profile.restSeconds || DEFAULT_REST_SECONDS);
    }
    // Auto-focus next non-completed top-level set's weight input.
    // Skip drop-set children (parentId set) — those aren't the next "main" set.
    if (willComplete) {
      const idx = beforeBlock.findIndex((s) => s.id === setId);
      const nextSet = beforeBlock.slice(idx + 1).find((s) => !s.parentId && !s.completed);
      if (nextSet) {
        // Small delay so React commits the disabled state on the completed
        // input before we try to focus the next one (mobile keyboards can
        // close the dismissal otherwise).
        setTimeout(() => {
          const el = document.querySelector(`[data-weight-input-id="${nextSet.id}"]`);
          if (el && typeof el.focus === 'function') {
            el.focus();
            if (typeof el.select === 'function') el.select();
          }
        }, 60);
      }
    }
  };

  const addSet = (blockIdx) => {
    setSetsByBlock((prev) => {
      const cur = prev[blockIdx] || [];
      const last = [...cur].reverse().find((s) => s.type === 'normal') || cur[cur.length - 1];
      const newSet = {
        id: newId(),
        parentId: null,
        type: 'normal',
        weight: last?.weight ?? 0,
        reps: last?.reps ?? '8',
        completed: false,
      };
      return { ...prev, [blockIdx]: [...cur, newSet] };
    });
  };

  const addDropset = (blockIdx, parentId) => {
    setSetsByBlock((prev) => {
      const cur = prev[blockIdx] || [];
      const parent = cur.find((s) => s.id === parentId);
      if (!parent) return prev;
      const dropWeight = Math.max(0, Math.round((parent.weight || 0) * 0.7 / 5) * 5); // -30%, rounded to 5
      const newSet = {
        id: newId(),
        parentId,
        type: 'drop',
        weight: dropWeight,
        reps: parent.reps,
        completed: false,
      };
      // Insert after parent (and after any existing drops of parent)
      const next = [...cur];
      let insertAt = next.findIndex((s) => s.id === parentId) + 1;
      while (insertAt < next.length && next[insertAt].parentId === parentId) insertAt++;
      next.splice(insertAt, 0, newSet);
      return { ...prev, [blockIdx]: next };
    });
  };

  const removeSet = (blockIdx, setId) => {
    setSetsByBlock((prev) => ({
      ...prev,
      [blockIdx]: prev[blockIdx].filter((s) => s.id !== setId && s.parentId !== setId),
    }));
  };

  // "Copy last session" bulk-fill — explicit opt-in convenience that mirrors
  // every completed set from the user's previous session of this exercise.
  // Skip child (drop) sets and any set already marked done so we don't undo
  // in-progress work. Pairs with the per-set quick-fill chips for granular
  // control.
  const copyLastSession = (blockIdx) => {
    const block = plan[blockIdx];
    if (!block) return;
    const lastTime = findLastTime(history, block.ex.id);
    if (!lastTime?.sets?.length) return;
    setSetsByBlock((prev) => {
      const cur = prev[blockIdx] || [];
      const updated = cur.map((s, idx) => {
        if (s.parentId || s.completed) return s; // skip drops + already-logged
        // Match by sequential normal-set index (mirrors SetRow's histIdx logic)
        const normalIdx = cur.slice(0, idx + 1).filter((x) => !x.parentId).length - 1;
        const hist = lastTime.sets[normalIdx];
        if (!hist) return s;
        return { ...s, weight: hist.weight, reps: hist.reps };
      });
      return { ...prev, [blockIdx]: updated };
    });
  };

  // Workout totals — skipped blocks excluded from both numerator and
  // denominator so the user's "you logged X of Y" reflects only what they
  // intended to do.
  const stats = useMemo(() => {
    let totalSets = 0, doneSets = 0, totalVolume = 0;
    Object.entries(setsByBlock).forEach(([blockIdxKey, arr]) => {
      if (skippedBlocks.has(Number(blockIdxKey))) return;
      arr.forEach((s) => {
        totalSets++;
        if (s.completed) {
          doneSets++;
          const repNum = parseInt(String(s.reps).split('-')[0]) || 0;
          totalVolume += (s.weight || 0) * repNum;
        }
      });
    });
    return { totalSets, doneSets, totalVolume };
  }, [setsByBlock, skippedBlocks]);

  // Circuit mode: true when every non-skipped exercise has its current-round set completed.
  // Used to show the "Round X complete → Begin Round X+1" banner.
  const allCurrentRoundDone = isCircuit && plan.length > 0 && plan.every((_, i) => {
    if (skippedBlocks.has(i)) return true;
    const roundSets = setsByBlock[i] || [];
    return roundSets[currentCircuitRound - 1]?.completed;
  });

  const toggleSkipBlock = (i) => {
    setSkippedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
    // Collapse if we just skipped the expanded block — no point keeping a
    // skipped row open.
    setExpandedBlock((cur) => (cur === i ? -1 : cur));
  };

  const handleFinish = () => {
    // Refuse to log workouts more than 14 days back — prevents confused users
    // from polluting their history. They can still adjust the date via the
    // dashboard if they really need a historical correction.
    if (daysAgo > 14) {
      alert('Workouts can only be logged within the last 14 days. Pick a more recent day to log this one.');
      return;
    }
    if (isLoggingFuture) {
      alert("You can't log a workout that hasn't happened yet. Pick today or an earlier day.");
      return;
    }
    // Close the Finish-confirm dialog FIRST so any celebration/program modals
    // that fire below aren't stacked behind it. Without this, the celebration
    // renders under the Finish dialog and only becomes visible after the
    // Finish dialog is dismissed (which closes both at once).
    setConfirmFinish(false);

    // Compute the ISO date for the selected day. Use end-of-day (23:59) so
    // that calendar grouping by date lands cleanly on the user's intended day
    // regardless of timezone.
    const logDate = new Date(selectedDate);
    logDate.setHours(23, 59, 0, 0);

    const payload = {
      date: logDate.toISOString(),
      // Capture TZ offset at log time so the Pacing Engine can correctly
      // bucket this workout into the right calendar week even if the user
      // travels to another timezone before viewing. See
      // backend/services/pacing.py + STANDING_INSTRUCTIONS.
      tz_offset_minutes: new Date().getTimezoneOffset(),
      title: aiOverride ? aiOverride.title : (todayTemplate?.title || sampleWorkout.title),
      duration: profile.minutesPerSession || 45,
      exerciseCount: plan.length,
      exercisesCompleted: plan.filter((_, i) => (setsByBlock[i] || []).some((s) => s.completed)).length,
      totalVolume: Math.round(stats.totalVolume),
      muscles: aiOverride ? aiOverride.muscles : (todayTemplate?.muscles || sampleWorkout.muscles),
      programType: programType,
      notes: workoutNote.trim() || undefined,
      exercises: plan.map((b, i) => ({
        refId: b.ex.id,
        name: b.ex.name,
        note: (notesByBlock[i] || '').trim() || undefined,
        sets: (setsByBlock[i] || []).map((s) => ({
          weight: s.weight,
          reps: s.reps,
          type: s.type,
          completed: s.completed,
        })),
      })),
    };
    completeWorkout(payload);
    // Clear the in-progress checkpoint so the next visit to this day starts fresh.
    try { localStorage.removeItem(inProgressKey); } catch { /* ignore */ }
    try { localStorage.removeItem(exerciseNotesKey); } catch { /* ignore */ }
    try { localStorage.removeItem(workoutNoteKey); } catch { /* ignore */ }
    // Retire the AI session once logged — the next visit returns to the schedule.
    try { localStorage.removeItem(`${inProgressKey}_aiplan`); } catch { /* ignore */ }
    setAiOverride(null);
    setNotesByBlock({});
    setWorkoutNote('');

    // Detect milestones AFTER logging — PR detection runs against history that
    // doesn't yet include today's session, which is exactly what we want.
    const prs = detectPRs(plan, setsByBlock, history);
    const sessionMs = detectSessionMilestone((history?.length || 0) + 1);
    // Streak milestone: compute the streak that WILL exist after today's
    // session is added to history (so we don't miss a crossing).
    const projectedHistory = [...(history || []), { date: localISO(), exercises: payload.exercises }];
    // Build a permissive schedule from history (every weekday user has trained
    // is treated as a workout day; others as rest). Same logic as StreakWidget.
    const sched = (() => {
      const seen = new Array(7).fill(false);
      for (const h of projectedHistory) {
        try {
          const d = new Date((typeof h.date === 'string' ? h.date : new Date(h.date).toISOString()).slice(0, 10) + 'T00:00:00');
          seen[d.getDay()] = true;
        } catch { /* ignore */ }
      }
      return seen.map((b) => b ? 'strength' : 'rest');
    })();
    const newStreakDays = computeAdherenceStreak(projectedHistory, sched, profile.streakPauses, localISO());
    const streakMs = newlyCrossedStreakMilestone(newStreakDays, profile.experience || 'intermediate', profile.streakLastCelebratedDays || 0);
    if (streakMs) {
      // Persist last-celebrated so we don't re-fire next session.
      try { markStreakCelebrated(streakMs.days); } catch { /* helper imported below */ }
    }
    // Also track best-ever streak.
    if (newStreakDays > (profile.streakBestEver || 0)) {
      try { updateStreakBestEver(newStreakDays); } catch { /* helper imported below */ }
    }
    const programDone = detectProgramCompletion(profile);
    if (prs.length > 0 || sessionMs || streakMs) {
      setCelebration({ prs, sessionMilestone: sessionMs, streakMilestone: streakMs });
      // If a program completion ALSO fires, queue it so it shows after the
      // celebration modal is dismissed.
      if (programDone) setProgramCompletionPrompt(programDone);
      return;
    }
    // Program completion: detect AFTER milestone checks (so a session that
    // both PRs AND completes a program shows PR first, then the program
    // completion modal). Detection is non-destructive — it just returns a
    // candidate; the user confirms or snoozes.
    if (programDone) {
      setProgramCompletionPrompt(programDone);
      return;
    }
    // Workout saved — stay on this page (walkthrough fix: prior
    // behavior auto-navigated to /progress, breaking multi-task flows). The
    // Day-Already-Logged gate will surface naturally on next render.
    toast.success('Workout logged', {
      description: 'See it on the Progress page',
      action: { label: 'View', onClick: () => navigate('/progress') },
    });
  };

  const progressPct = stats.totalSets ? (stats.doneSets / stats.totalSets) * 100 : 0;
  const mm = String(Math.floor(restSeconds / 60)).padStart(2, '0');
  const ss = String(restSeconds % 60).padStart(2, '0');
  const restPct = restTarget ? ((restTarget - restSeconds) / restTarget) * 100 : 0;

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      {/* One-time pull-up bar nudge for no-equipment PPL Pull Day users.
          Fires when the user lands on Pull Day. X dismisses permanently.
          CTA opens the recommendations card (pullup-bar item lives in calisthenics group). */}
      <Dialog open={pullBarPopupOpen} onOpenChange={(open) => { if (!open) dismissPullBarPopup(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-wide leading-tight">
              Build your Pull chain.
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-1">
                <p className="text-sm text-enf-text-secondary leading-relaxed">
                  A doorway pull-up bar (~$25) is the single fastest gear upgrade for Pull Day — it unlocks Pull-Ups, Chin-Ups, and the full vertical pulling chain this session is built around. No tools, no installation.
                </p>
                <p className="text-sm text-enf-text-secondary leading-relaxed">
                  Until yours arrives: add an extra set to each movement below. Same time slot, same heat.
                </p>
                <p className="text-xs text-enf-text-muted leading-relaxed italic">
                  We'll only show this once. Bar options are always in the Recommendations section on this page.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={openPullBarFromPopup}
              className="flex-1 px-4 py-2.5 bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary text-sm font-semibold rounded-lg transition-colors"
            >
              See bar options
            </button>
            <button
              type="button"
              onClick={dismissPullBarPopup}
              className="px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border text-enf-text-secondary text-sm rounded-lg transition-colors"
            >
              Not now
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* One-time band upgrade popup for no-equipment users.
          Shown once per user lifetime; gated by affiliates feature flag.
          X dismisses permanently. CTA opens the recommendations card. */}
      <Dialog open={bandPopupOpen} onOpenChange={(open) => { if (!open) dismissBandPopup(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-wide leading-tight">
              One small upgrade, a lot more range.
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-1">
                <p className="text-sm text-enf-text-secondary leading-relaxed">
                  Bodyweight training is real training. A resistance band set costs less than a single gym visit and adds load, variety, and pulling work that bodyweight alone can't match — especially for glutes, rows, and shoulder health.
                </p>
                <p className="text-xs text-enf-text-muted leading-relaxed italic">
                  We'll only show this once. Band options are always available in the Recommendations section on this page.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={openBandsFromPopup}
              className="flex-1 px-4 py-2.5 bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary text-sm font-semibold rounded-lg transition-colors"
            >
              See band options
            </button>
            <button
              type="button"
              onClick={dismissBandPopup}
              className="px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border text-enf-text-secondary text-sm rounded-lg transition-colors"
            >
              Not now
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="max-w-7xl mx-auto px-6">
        <HistoryClampBanner />
        {/* Banner order (Jul 2026): warnings/eases (red→amber) first, then the
            blue check-in prompt, then the "Today's Adjustments" summary, then the
            lighter-day control last — nearest the loggable workout, least
            scrolling to the most-used toggle. */}

        {/* Overtraining warnings */}
        {overtrainingWarnings.length > 0 && (
          <div className="mb-5 space-y-2">
            {overtrainingWarnings.map((w, i) => (
              <div key={i} className={`p-4 rounded-lg border flex items-start gap-3 ${
                w.level === 'high' ? 'bg-enf-role-danger/10 border-enf-role-danger/40' : 'bg-enf-role-warning/5 border-enf-role-warning/30'
              }`}>
                <AlertCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${w.level === 'high' ? 'text-enf-role-danger' : 'text-enf-role-warning'}`} />
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ color: w.level === 'high' ? '#fca5a5' : '#fbbf24' }}>{w.title}</div>
                  <div className="text-sm text-enf-text-secondary">{w.message}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CI-9: closed-loop auto-regulation notice — shows only when the loop
            actually eased the session (amber/red). Reduce-only training
            adjustment from the daily check-in, not medical advice. */}
        {autoReg.level !== 'green' && (
          <div
            data-testid="auto-regulation-banner"
            className={`mb-5 p-4 rounded-lg border flex items-start gap-3 ${
              autoReg.level === 'red'
                ? 'bg-enf-role-danger/10 border-enf-role-danger/40'
                : 'bg-enf-role-warning/5 border-enf-role-warning/30'
            }`}
          >
            <Activity className={`w-4 h-4 mt-0.5 flex-shrink-0 ${autoReg.level === 'red' ? 'text-enf-role-danger' : 'text-enf-role-warning'}`} />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest font-semibold mb-1 text-enf-text-secondary">
                {sessionOverride === 'full'
                  ? 'Full session restored'
                  : autoReg.level === 'red' ? 'Today\u2019s a recovery day' : 'Today\u2019s session is eased back'}
              </div>
              <div className="text-sm text-enf-text-primary">
                {sessionOverride === 'full'
                  ? `Heads up \u2014 ${autoReg.reason}. We\u2019d normally ease today\u2019s volume; you\u2019re running the full session.`
                  : `Because ${autoReg.reason}, we trimmed today\u2019s volume${autoReg.level === 'red' ? ' to core work only' : ''} \u2014 a lighter option, not a smaller you.`}
              </div>
              <div className="text-[11px] text-enf-text-muted mt-1">Training adjustment from your check-in — not medical advice.</div>
              <button
                type="button"
                onClick={() => setSessionOverride((v) => (v === 'full' ? null : 'full'))}
                data-testid="auto-regulation-toggle"
                className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-enf-role-info hover:text-enf-text-primary transition-colors"
              >
                {sessionOverride === 'full' ? 'Ease back to recommended' : 'Restore full session'}
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        {/* CI-9 Phase A: regional auto-regulation banner — amputee users only,
            only when residual-limb signals (phantom / socket / skin) are elevated.
            Separate from the systemic banner above; explicitly names which signals
            fired and that only prosthesis-loaded movements are affected. */}
        {isAmputee && regionalReg.level !== 'green' && (
          <div
            data-testid="regional-regulation-banner"
            className={`mb-5 p-4 rounded-lg border flex items-start gap-3 ${
              regionalReg.level === 'red'
                ? 'bg-enf-role-danger/10 border-enf-role-danger/40'
                : 'bg-enf-role-warning/5 border-enf-role-warning/30'
            }`}
          >
            <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${regionalReg.level === 'red' ? 'text-enf-role-danger' : 'text-enf-role-warning'}`} />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest font-semibold mb-1 text-enf-text-secondary">
                {regionalOverride
                  ? 'Prosthesis-loaded work — full volume restored'
                  : regionalReg.level === 'red'
                    ? 'Prosthesis-loaded work eased back (recovery)'
                    : 'Prosthesis-loaded work eased back'}
              </div>
              <div className="text-sm text-enf-text-primary">
                {regionalOverride
                  ? `Your residual-limb check-in flagged ${regionalReg.reason} — we'd normally ease your prosthesis-loaded movements. You're running full volume.`
                  : autoReg.level !== 'green'
                    ? `Your check-ins — readiness, pain, and ${regionalReg.reason} — pointed the same direction. Prosthesis-loaded movements are eased back further today; everything else is unchanged.`
                    : `Because ${regionalReg.reason}, we eased your prosthesis-loaded movements. Everything else is at full volume.`}
              </div>
              <div className="text-[11px] text-enf-text-muted mt-1">Training adjustment from your residual-limb check-in — not medical advice.</div>
              <button
                type="button"
                onClick={() => setRegionalOverride((v) => !v)}
                data-testid="regional-regulation-toggle"
                className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-enf-role-info hover:text-enf-text-primary transition-colors"
              >
                {regionalOverride ? 'Ease back to recommended' : 'Restore full volume for these movements'}
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        {/* Pain logged — read-only summary + recommendation; edit on Progress (CI-5) */}
        {painResult && (
          <div
            data-testid="pain-banner"
            className={`mb-5 p-4 rounded-lg border flex items-start gap-3 ${
              painResult.tier === 'red' ? 'bg-enf-role-danger/10 border-enf-role-danger/40' :
              painResult.tier === 'amber' ? 'bg-enf-role-warning/5 border-enf-role-warning/30' :
              'bg-enf-surface-special/30 border-enf-border-special/30'
            }`}>
            <Heart className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
              painResult.tier === 'red' ? 'text-enf-role-danger' :
              painResult.tier === 'amber' ? 'text-enf-role-warning' : 'text-enf-role-info'
            }`} />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest font-semibold mb-1 text-enf-text-secondary">Today's pain · level {todayPainLevel}/10</div>
              <div className="text-sm text-enf-text-primary">{painResult.message}</div>
              <Link
                to="/progress"
                data-testid="pain-edit-link"
                className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-enf-text-secondary hover:text-enf-text-primary transition-colors"
              >
                Adjust on Progress <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        )}

        {/* Pain not logged yet — info prompt only; editing lives on Progress (CI-5) */}
        {showInlinePainBanner && (
          <div className="mb-5 p-4 rounded-lg bg-enf-surface-special/30 border border-enf-border-special/30 flex flex-wrap items-center gap-3" data-testid="pain-inline-banner">
            <Heart className="w-4 h-4 text-enf-role-info flex-shrink-0" />
            <div className="flex-1 min-w-[180px]">
              <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mb-0.5">Today's body check</div>
              <div className="text-sm text-enf-text-secondary">Log today's pain on Progress and we'll adjust your intensity if you need it.</div>
            </div>
            <Link
              to="/progress"
              data-testid="pain-inline-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-xs font-semibold transition-colors flex-shrink-0"
            >
              Log on Progress <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {/* Manual ease-back (lighter day) — the control nearest the workout, so
            the most-used toggle needs the least scrolling. Green-readiness days
            with overtraining flags only; when readiness/pain already eased the
            session, the CI-9 banner above owns the control. */}
        {overtrainingWarnings.length > 0 && tierLimit > 1 && autoReg.level === 'green' && (
          <button
            type="button"
            onClick={() => setSessionOverride((v) => (v === 'lighter' ? null : 'lighter'))}
            data-testid="overtraining-lighter-day"
            className={`w-full text-left mb-5 p-3 rounded-lg border transition-colors flex items-center justify-between gap-3 ${
              sessionOverride === 'lighter'
                ? 'bg-enf-accent/10 border-enf-accent/40 text-enf-text-primary'
                : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-accent/40'
            }`}
          >
            <span className="text-sm">
              {sessionOverride === 'lighter'
                ? "Lighter day on — trimmed today's accessory work. Tap to undo."
                : "Want to ease off? Take a lighter day — we'll trim today's accessory work."}
            </span>
            <span className="text-xs uppercase tracking-widest font-semibold flex-shrink-0">{sessionOverride === 'lighter' ? 'Undo' : 'Lighter day'}</span>
          </button>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-2">
              <Sparkles className="w-3.5 h-3.5" /> {program.name} · {DAYS[selectedDayIdx]?.long}
            </div>
            <h1 className="font-display text-5xl md:text-7xl tracking-wide leading-none mb-3">
              {aiOverride ? aiOverride.title : (isRecoveryDay ? 'Endurance' : isEnduranceDay ? 'Endurance Day' : isBodyweightRedirect ? programs[programType]?.name || 'Training' : isRestDay ? 'Rest Day' : todayTemplate?.title || sampleWorkout.title)}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-enf-text-secondary">
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {profile.minutesPerSession || 45} min</span>
              <button
                type="button"
                onClick={() => setShowIntensityBreakdown((v) => !v)}
                data-testid="intensity-flame"
                aria-label={`Readiness: ${intensity.label}. Tap for breakdown.`}
                className="flex items-center gap-1.5 hover:text-enf-text-primary transition-colors"
              >
                <Flame className={`w-4 h-4 scale-x-[-1] ${intensity.color}`} /> {intensity.label}
              </button>
              {!isRestDay && isCircuit && <span>{plan.length} exercises · {circuitRounds} rounds</span>}
              {!isRestDay && !isCircuit && <span>{plan.length} exercises</span>}
              {!isRestDay && <span className="font-mono">{stats.doneSets}/{stats.totalSets} sets · {displayWeight(stats.totalVolume, units).toLocaleString()} {wUnit}</span>}
            </div>
            {showIntensityBreakdown && (
              <div className="mt-3 p-3 rounded-md bg-enf-surface-deep border border-enf-surface-border text-xs text-enf-text-secondary max-w-md" data-testid="intensity-breakdown">
                <div className="font-semibold mb-1 text-enf-text-primary">
                  {intensity.label}{intensity.score !== null ? ` · ${intensity.score}/100` : ''}
                </div>
                <div className="text-enf-text-secondary mb-2">{intensity.message}</div>
                <div className="text-enf-text-muted">
                  Readiness blends your daily check-in, today&rsquo;s pain, and connected health data when available.
                </div>
                {(todayPainLevel === null || !todayWellbeingEntry) && (
                  <div className="mt-2 pt-2 border-t border-enf-surface-border text-enf-text-muted">
                    {todayPainLevel === null && <div>· Pain not logged yet today</div>}
                    {!todayWellbeingEntry && <div>· Daily check-in not done yet</div>}
                    <Link
                      to="/progress"
                      data-testid="readiness-progress-link"
                      className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-enf-role-info hover:text-enf-text-primary transition-colors"
                    >
                      Log on Progress <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
          <ContextualPrompt
            id="no-equip-bodyweight-session"
            visible={!!profile.noEquipment && !isPaid && programType !== 'calisthenics'}
            title="Bodyweight session"
            body={`No equipment detected — today's ${programs[programType]?.name || 'session'} is built from no-equipment moves. Add gear to your profile to unlock the full program.`}
            action={{ label: 'Add equipment', onClick: () => setShowEquipmentEdit(true) }}
            dismissLabel="Got it"
            testid="prompt-no-equip-bw"
          />
          <ContextualPrompt
            id="equipment-strength-first-visit"
            visible={!profile.fullGym && !profile.noEquipment && (!Array.isArray(profile.equipment) || profile.equipment.length === 0)}
            title="Tell the coach what you've got"
            body="You picked 'Some equipment' but we don't know the specifics. Take 30 seconds to mark what you can train with — programs get sharper, suggested exercises get more useful."
            action={{ label: 'Mark my gear', onClick: () => setShowEquipmentEdit(true) }}
            testid="prompt-equipment"
          />
          <ContextualPrompt
            id="ai-workout-first-prompt"
            visible={Array.isArray(history) && history.length === 0 && profile.completedOnboarding}
            title="First workout? Let the coach build it."
            body="Tap 'Ignite a Custom Day' — Claude reads your profile and writes a session matched to today. Faster than guessing."
            action={{ label: 'Ignite a Custom Day', onClick: openAiOrPaywall }}
            dismissLabel="I'll pick my own"
            testid="prompt-ai-workout-first"
          />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowScheduleEditor(true)} className="px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium flex items-center gap-2 transition-colors">
              <Calendar className="w-4 h-4" /> Schedule
            </button>
            <button onClick={() => setShowProgramPicker(true)} className="px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium flex items-center gap-2 transition-colors">
              <RefreshCw className="w-4 h-4" /> Program
            </button>
            <button onClick={() => setShowAddExercise(true)} className="px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border hover:border-enf-role-info/50 rounded-md text-sm font-medium flex items-center gap-2 transition-colors" data-testid="strength-add-exercise-button">
              <Plus className="w-4 h-4 text-enf-role-info" /> Exercise
            </button>
            <button onClick={() => setShowEquipmentEdit(true)} className="px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border hover:border-enf-role-info/50 rounded-md text-sm font-medium flex items-center gap-2 transition-colors" data-testid="strength-equipment-button">
              <Wrench className="w-4 h-4 text-enf-role-info" /> Equipment
            </button>
            <button onClick={openAiOrPaywall} className="px-4 py-2.5 bg-enf-accent/10 hover:bg-enf-accent border border-enf-accent-fg/40 hover:border-enf-accent-fg text-enf-accent-fg hover:text-enf-text-primary rounded-md text-sm font-semibold flex items-center gap-2 transition-colors" data-testid="strength-ai-workout-button">
              <Sparkles className="w-4 h-4" /> Ignite a Custom Day
            </button>
          </div>
        </div>

        {/* Weekly day picker — full Mon→Sun week including rest & recovery slots */}
        <div className="mb-6 p-3 bg-enf-surface-deep border border-enf-surface-border rounded-lg">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold">This week · {program.name}</span>
              {Array.isArray(profile.schedule) && profile.schedule.length === 7 ? (
                <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest font-semibold bg-enf-role-info/15 text-enf-role-info border border-enf-role-info/30">Custom rotation</span>
              ) : (
                <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest font-semibold bg-enf-surface-mid text-enf-text-muted border border-enf-surface-border">Program default</span>
              )}
            </div>
            <span className="text-xs text-enf-text-muted">Tap a day to load it · endurance days open the Endurance page</span>
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {userSchedule.map((tplKey, i) => {
              const tpl = program.templates[tplKey];
              const isToday = i === realTodayIdx;
              const isSelected = i === selectedDayIdx;
              const isRecovery = tplKey === 'recovery';
              const isEnduranceSlot = tplKey === 'endurance';
              const isRest = tplKey === 'rest';
              const label = isRecovery ? 'Endurance' : isEnduranceSlot ? 'Endurance' : isRest ? 'Rest' : (tpl?.title.split(' ')[0] || 'Rest');
              const handleClick = () => {
                if (isRecovery) { navigate('/endurance'); return; }
                setSelectedDayIdx(i);
              };
              return (
                <button key={i} onClick={handleClick}
                  className={`p-2 rounded-md border text-center transition-all ${
                    isSelected ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' :
                    isRecovery ? 'bg-enf-surface-special/30 border-enf-role-info/30 text-enf-role-info hover:border-enf-role-info' :
                    isEnduranceSlot ? 'bg-enf-surface-special/20 border-enf-role-info/20 text-enf-role-info hover:border-enf-role-info' :
                    isRest ? 'bg-enf-surface-mid/60 border-enf-surface-border text-enf-text-muted hover:text-enf-text-secondary' :
                    isToday ? 'bg-enf-surface-special/40 border-enf-border-special/40 text-enf-text-primary hover:border-enf-border-special' :
                    'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary hover:border-enf-surface-border'
                  }`}>
                  <div className="text-[10px] uppercase tracking-widest font-semibold mb-1 opacity-80">{DAYS[i].short}{isToday && ' ·'}</div>
                  <div className="text-xs font-bold truncate">{label}</div>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-3">
            {selectedDayIdx !== realTodayIdx && !customLogDate && (
              <button onClick={() => setSelectedDayIdx(realTodayIdx)} className="text-xs text-enf-role-info hover:text-enf-text-primary transition-colors flex items-center gap-1">
                <ArrowUpDown className="w-3 h-3" /> Reset to today's prescribed workout
              </button>
            )}
            <button
              onClick={() => setShowHistoricalPicker(true)}
              className="text-xs text-enf-role-info hover:text-enf-text-primary transition-colors flex items-center gap-1"
              title="Log a workout you did on a past date — up to 14 days back"
              data-testid="log-past-workout-button"
            >
              <History className="w-3 h-3" /> Log a past workout
            </button>
            {!(Array.isArray(profile.schedule) && profile.schedule.length === 7) ? (
              <button
                onClick={() => { update({ schedule: userSchedule }); setCopyConfirmed(true); setTimeout(() => setCopyConfirmed(false), 2400); }}
                className="text-xs text-enf-text-secondary hover:text-enf-role-info transition-colors flex items-center gap-1"
                title="Save the current week as your repeating rotation so future weeks don't fall back to the program default"
              >
                <Calendar className="w-3 h-3" /> Copy this week to next
              </button>
            ) : (
              <button
                onClick={() => update({ schedule: null })}
                className="text-xs text-enf-text-muted hover:text-enf-role-warning transition-colors flex items-center gap-1"
                title="Drop your custom rotation and fall back to the program's default week"
              >
                <RotateCcw className="w-3 h-3" /> Revert to program default
              </button>
            )}
            {copyConfirmed && (
              <span className="text-xs text-enf-role-info flex items-center gap-1">
                <Check className="w-3 h-3" /> Locked in — this rotation will repeat every week.
              </span>
            )}
          </div>
          {customLogDate && (
            <div className="mt-3 flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-enf-role-info/10 border border-enf-role-info/30" data-testid="historical-logging-banner">
              <div className="text-xs text-enf-role-info flex items-center gap-2">
                <History className="w-3.5 h-3.5" />
                Logging for <span className="font-semibold">{customLogDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
                <span className="text-enf-text-muted">· template preset from {DAYS[selectedDayIdx]?.long}</span>
              </div>
              <button
                onClick={() => { setCustomLogDate(null); setSelectedDayIdx(realTodayIdx); }}
                className="text-xs text-enf-text-secondary hover:text-enf-text-primary transition-colors flex items-center gap-1"
                data-testid="cancel-historical-logging-button"
              >
                <X className="w-3 h-3" /> Cancel
              </button>
            </div>
          )}
        </div>

        {/* Session length — edit-in-place (no more profile-page bounce) */}
        <WidgetGate page="strength" id="sessionLengthPicker" className="mb-2">
          <div className="flex flex-wrap items-center gap-2 p-3 bg-enf-surface-deep border border-enf-surface-border rounded-lg" data-testid="session-length-picker">
            <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mr-2">Session length</span>
            {[
              { v: 30, t: '30 min' },
              { v: 45, t: '45 min' },
              { v: 60, t: '60 min' },
            ].map((o) => {
              const active = profile.minutesPerSession === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => update({ minutesPerSession: o.v })}
                  data-testid={`session-length-${o.v}`}
                  aria-pressed={active}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    active ? 'bg-enf-accent text-enf-text-primary' : 'bg-enf-surface-mid text-enf-text-secondary hover:text-enf-text-primary'
                  }`}
                >
                  {o.v} min
                </button>
              );
            })}
          </div>
        </WidgetGate>

        {/* Auto-save reassurance strip — only shows when there's in-progress data */}
        {stats.doneSets > 0 && (
          <WidgetGate page="strength" id="autosaveIndicator" className="mb-6">
            <div className="px-3 py-2 bg-enf-role-info/5 border border-enf-role-info/20 rounded-md flex items-center gap-2 text-xs text-enf-role-info" data-testid="autosave-indicator">
              <Check className="w-3.5 h-3.5" />
              <span>{stats.doneSets} sets saved locally. Tap <strong className="text-enf-text-primary">Finish workout</strong> when you're done to log it permanently.</span>
            </div>
          </WidgetGate>
        )}

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-enf-text-muted mb-2">
            <span>Workout progress</span>
            <span className="font-mono">{stats.doneSets} / {stats.totalSets} sets</span>
          </div>
          <div className="h-1 bg-enf-surface-mid rounded-full overflow-hidden">
            <div className="h-full bg-enf-accent transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* grid-cols-1 explicit on mobile + min-w-0 on each grid item is the
            real fix for the recurring "exercise cards spill off the right
            edge on mobile" bug. Default `grid` without column specs uses
            implicit auto-sized columns that GROW to fit content, so a single
            non-shrinking descendant (sticky chip, untruncated label) was
            dragging the whole column past the viewport. Constraining the
            column to 1fr (mobile) and adding min-w-0 lets every nested
            flex/truncate actually engage. fix. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Exercise list */}
          <div className="lg:col-span-2 min-w-0 space-y-3">
            {(isRecoveryDay && !aiOverride) ? (
              <div className="bg-enf-surface-deep border border-enf-role-info/30 rounded-xl p-8 text-center">
                <Activity className="w-8 h-8 text-enf-role-info mx-auto mb-3" />
                <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-2">Endurance Day</div>
                <h3 className="font-display text-3xl tracking-wide leading-none mb-3">Move with intent.</h3>
                <p className="text-sm text-enf-text-secondary max-w-md mx-auto mb-5">
                  Today is for your endurance work — walk, ride, swim, climb, row. Pick your activity, set your intensity (Easy · Steady · Sport), and go.
                </p>
                <button onClick={() => navigate('/endurance')}
                  className="inline-flex items-center gap-2 px-5 py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm transition-colors">
                  Open Endurance <ChevronRight className="w-4 h-4" />
                </button>
                <div className="mt-5 text-xs text-enf-text-muted">
                  Need to lift today instead? <button onClick={() => setShowScheduleEditor(true)} className="underline hover:text-enf-role-info">Edit schedule</button>
                </div>
              </div>
            ) : (isEnduranceDay && !aiOverride) ? (
              <div className="bg-enf-surface-deep border border-enf-role-info/30 rounded-xl p-8 text-center">
                <Activity className="w-8 h-8 text-enf-role-info mx-auto mb-3" />
                <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-2">Endurance Day</div>
                <h3 className="font-display text-3xl tracking-wide leading-none mb-3">Keep the fire burning.</h3>
                <p className="text-sm text-enf-text-secondary max-w-md mx-auto mb-5">
                  Your program built in today for cross-training. Head to the Endurance tab and pick any activity that suits your setup — whatever moves you counts.
                </p>
                <button onClick={() => navigate('/endurance')}
                  className="inline-flex items-center gap-2 px-5 py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm transition-colors">
                  Open Endurance <ChevronRight className="w-4 h-4" />
                </button>
                <div className="mt-5 text-xs text-enf-text-muted">
                  Prefer to strength train today? <button onClick={() => setShowScheduleEditor(true)} className="underline hover:text-enf-role-info">Edit schedule</button>
                </div>
              </div>
            ) : (isBodyweightRedirect && !aiOverride) ? (
              <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-8 text-center">
                <Dumbbell className="w-8 h-8 text-enf-text-muted mx-auto mb-3" />
                <div className="text-xs uppercase tracking-[0.3em] text-enf-text-muted font-semibold mb-2">Program Mismatch</div>
                <h3 className="font-display text-3xl tracking-wide leading-none mb-3">This one needs equipment.</h3>
                <p className="text-sm text-enf-text-secondary max-w-md mx-auto mb-2">
                  {programs[programType]?.name} is built around equipment-based training. With a bodyweight-only setup, the sessions are too thin to give you a quality workout.
                </p>
                <p className="text-sm text-enf-text-secondary max-w-md mx-auto mb-6">
                  These programs are built specifically for what you've got:
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 mb-6">
                  <button
                    onClick={() => update({ programType: 'calisthenics', schedule: null })}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm transition-colors w-full sm:w-auto">
                    Calisthenics Circuit <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => update({ programType: 'full-body', schedule: null })}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md font-semibold text-sm transition-colors w-full sm:w-auto">
                    Full Body <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => update({ programType: 'upper-lower', schedule: null })}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md font-semibold text-sm transition-colors w-full sm:w-auto">
                    Upper / Lower Split <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-enf-text-muted">
                  Or <button onClick={() => setShowEquipmentEdit(true)} className="underline hover:text-enf-text-secondary">add equipment here</button> to unlock the full {programs[programType]?.name} program.
                </p>
              </div>
            ) : (isRestDay && !aiOverride) ? (
              <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-8 text-center">
                <Heart className="w-8 h-8 text-enf-role-warning mx-auto mb-3" />
                <div className="text-xs uppercase tracking-[0.3em] text-enf-role-warning font-semibold mb-2">Rest Day</div>
                <h3 className="font-display text-3xl tracking-wide leading-none mb-3">Today you grow.</h3>
                <p className="text-sm text-enf-text-secondary max-w-md mx-auto mb-3">
                  Muscles repair, the nervous system resets, and tomorrow's session comes back stronger. Hydrate, sleep, and stay off the bar.
                </p>
                <p className="text-sm text-enf-text-secondary max-w-md mx-auto mb-5">
                  Feeling like some easy movement? A stretch or mobility flow is a perfect fit for a rest day. Start a ready-made full-body stretch routine — free, any day — or let Coach ignite a custom session tailored to you.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2.5">
                  <button onClick={() => handleStartRestStretch()}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary rounded-md font-semibold text-sm transition-colors" data-testid="rest-start-stretch">
                    <Activity className="w-4 h-4" /> Start a stretch routine
                  </button>
                  <button onClick={openAiOrPaywall}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-enf-accent/10 hover:bg-enf-accent border border-enf-accent-fg/40 hover:border-enf-accent-fg text-enf-accent-fg hover:text-enf-text-primary rounded-md font-semibold text-sm transition-colors" data-testid="rest-ignite-stretch">
                    <Sparkles className="w-4 h-4" /> Ignite a custom session
                  </button>
                  <button onClick={() => navigate('/library?filter=stretching')}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md font-semibold text-sm transition-colors" data-testid="rest-browse-stretches">
                    <BookOpen className="w-4 h-4" /> Browse stretches
                  </button>
                </div>
                <button onClick={() => setShowScheduleEditor(true)}
                  className="mt-4 text-xs text-enf-text-secondary underline hover:text-enf-role-info">
                  Need to lift instead? Edit schedule
                </button>
              </div>
            ) : (dayAlreadyLogged && !aiOverride) ? (
              <DayAlreadyLoggedGate
                workouts={sameDayWorkouts}
                selectedDate={selectedDate}
                units={units}
                onEdit={(entry) => setEditingLoggedEntry(entry)}
                onLogAnother={() => setLogAnotherUnlocked(true)}
              />
            ) : (
              <>
                {logAnotherUnlocked && sameDayWorkouts.length > 0 && (
                  <div className="px-4 py-2.5 bg-enf-role-info/10 border border-enf-role-info/30 rounded-md flex items-center justify-between gap-3 text-xs text-enf-text-secondary" data-testid="logging-second-banner">
                    <span><strong className="text-enf-role-info uppercase tracking-widest text-[10px]">2nd Session</strong> · Already logged once today. You're now starting a separate workout for the same day.</span>
                    <button
                      onClick={() => setLogAnotherUnlocked(false)}
                      className="text-enf-text-muted hover:text-enf-text-primary"
                      data-testid="logging-second-cancel"
                      title="Cancel and return to logged view"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <WidgetGate page="strength" id="coachTip">
                  <CoachTip templateKey={todayTemplateKey} />
                </WidgetGate>
                {aiOverride && (
                  <div className="mb-3 flex items-center justify-between gap-3 px-4 py-2.5 bg-enf-accent/10 border border-enf-accent-fg/30 rounded-lg" data-testid="ai-session-banner">
                    <div className="flex items-center gap-2 min-w-0">
                      {aiOverride.source === 'static-stretch'
                        ? <Activity className="w-4 h-4 text-enf-accent-fg flex-shrink-0" />
                        : <Sparkles className="w-4 h-4 text-enf-accent-fg flex-shrink-0" />}
                      <span className="text-sm truncate">
                        <span className="font-semibold">{aiOverride.source === 'static-stretch' ? 'Stretch routine' : 'AI session'}</span> · {aiOverride.title}
                      </span>
                    </div>
                    <button
                      onClick={handleRevertAiWorkout}
                      className="text-xs font-medium text-enf-text-secondary hover:text-enf-text-primary whitespace-nowrap"
                      data-testid="ai-session-revert"
                    >
                      Revert to scheduled
                    </button>
                  </div>
                )}
                {aiOverride?.source === 'static-stretch'
                  && ((aiOverride.dropped?.length || 0) + (aiOverride.swapped?.length || 0)) > 0 && (
                  <TodaysAdjustments
                    adjustments={{
                      count: (aiOverride.dropped?.length || 0) + (aiOverride.swapped?.length || 0),
                      groups: [
                        ...(aiOverride.swapped?.length ? [{
                          key: 'stretch-swapped',
                          title: 'Swapped for a safer fit',
                          reason: 'These were exchanged for stretches that spare an area you flagged.',
                          items: aiOverride.swapped.map((s) => ({ name: `${s.from} → ${s.to}`, detail: 'safer sub' })),
                        }] : []),
                        ...(aiOverride.dropped?.length ? [{
                          key: 'stretch-dropped',
                          title: 'Reduced for your flagged areas',
                          reason: 'A few stretches that load an area you flagged were left out of this routine — restore the full routine below if you want them.',
                          items: aiOverride.dropped.map((d) => ({ name: d.name, detail: (d.areas || []).join(', ') || 'flagged area' })),
                        }] : []),
                      ],
                    }}
                    onRestore={aiOverride.dropped?.length ? () => handleStartRestStretch(true) : undefined}
                    restoreLabel="Restore full routine"
                  />
                )}
                {/* Today's Adjustments — sits just above the loggable sets (below
                    the day title, schedule, session length, coach tip, and the
                    revert bar) so "what changed vs. the scheduled day" reads right
                    next to the workout + revert control. Empty on rest/AI days —
                    the AI-session block above owns its own adjustments there. */}
                <TodaysAdjustments adjustments={todaysAdjustments} />
                {/* ── Circuit mode round tracker ── */}
                {isCircuit && plan.length > 0 && (
                  <div className="mb-4 space-y-3">
                    <div className="flex items-center justify-between gap-3 p-4 bg-enf-surface-deep border border-enf-accent-fg/30 rounded-xl">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-enf-text-muted mb-1">Circuit</div>
                        <div className="font-display text-2xl tracking-wide text-enf-text-primary">
                          Round {currentCircuitRound}
                          <span className="text-enf-text-muted text-base font-normal"> of {circuitRounds}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {Array.from({ length: circuitRounds }, (_, i) => {
                          const roundPast = i < currentCircuitRound - 1;
                          const roundActive = i === currentCircuitRound - 1;
                          return (
                            <div key={i} className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${
                              roundPast ? 'bg-enf-role-success border-enf-role-success text-white'
                                : roundActive ? 'bg-enf-accent/20 border-enf-accent-fg text-enf-accent-fg'
                                : 'bg-transparent border-enf-surface-border text-enf-text-muted'
                            }`}>
                              {roundPast ? <Check className="w-4 h-4" /> : i + 1}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {allCurrentRoundDone && currentCircuitRound < circuitRounds && (
                      <div className="p-4 bg-enf-role-success/10 border border-enf-role-success/30 rounded-xl flex items-center justify-between gap-3">
                        <span className="text-sm font-semibold text-enf-role-success">Round {currentCircuitRound} complete!</span>
                        <button
                          onClick={() => { startRest(profile.restSeconds || DEFAULT_REST_SECONDS); setCurrentCircuitRound((r) => r + 1); }}
                          className="px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary rounded-lg text-sm font-semibold transition-colors"
                          data-testid="circuit-next-round"
                        >
                          Begin Round {currentCircuitRound + 1} →
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {plan.map((rawBlock, i) => {
                  // Apply any user-swapped exercise override for this slot.
                  const b = swappedExercises[i] ? { ...rawBlock, ex: swappedExercises[i] } : rawBlock;
                  const sets = setsByBlock[i] || [];
                  const isSkipped = skippedBlocks.has(i);
                  const exDone = !isSkipped && sets.length > 0 && sets.every((s) => s.completed);
                  const isExpanded = expandedBlock === i;
                  const lt = findLastTime(history, b.ex.id);
                  const openSwap = () => { setReplaceTarget(i); setShowAddExercise(true); };
                  if (isCircuit) {
                    return (
                      <CircuitExerciseRow
                        key={i}
                        blockIdx={i}
                        block={b}
                        sets={sets}
                        currentRound={currentCircuitRound}
                        circuitRounds={circuitRounds}
                        skipped={isSkipped}
                        exerciseDone={exDone}
                        onToggleSkip={() => toggleSkipBlock(i)}
                        onPreview={() => setPreviewEx(b.ex)}
                        onMarkSetDone={(setId) => markSetDone(i, setId)}
                        onSwap={openSwap}
                      />
                    );
                  }
                  return (
                    <ExerciseRow
                      key={i}
                      blockIdx={i}
                      block={b}
                      sets={sets}
                      expanded={isExpanded && !isSkipped}
                      exerciseDone={exDone}
                      skipped={isSkipped}
                      level={profile.experience || 'intermediate'}
                      onToggleSkip={() => toggleSkipBlock(i)}
                      onToggleExpand={() => setExpandedBlock(isExpanded ? -1 : i)}
                      onPreview={() => setPreviewEx(b.ex)}
                      onSwap={openSwap}
                      onUpdateSet={(setId, patch) => updateSet(i, setId, patch)}
                      onMarkSetDone={(setId) => markSetDone(i, setId)}
                      onAddSet={() => addSet(i)}
                      onAddDropset={(parentId) => addDropset(i, parentId)}
                      onRemoveSet={(setId) => removeSet(i, setId)}
                      onCopyLastSession={() => copyLastSession(i)}
                      note={notesByBlock[i] || ''}
                      onChangeNote={(value) => setNotesByBlock((prev) => ({ ...prev, [i]: value }))}
                      lastTime={lt}
                      lastTimeStr={formatLastTime(lt, units)}
                      units={units}
                    />
                  );
                })}
                {plan.length === 0 && (() => {
                  // Blank-day breath-work fallback (Jul 2026): rather than an empty
                  // "nothing fits" state, recommend a breathing reset. The four breath
                  // drills (291-294) are bodyweight + zero-contraindication, so they
                  // survive every filter — pain, full-body flags, amputee, no gear.
                  // Anti-shame: framed as a deliberate, protective choice, never failure.
                  const breathDrills = [291, 292, 293, 294].map(getExerciseById).filter(Boolean);
                  return (
                    <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
                      <div className="flex items-start gap-3 mb-4">
                        <HeartPulse className="w-6 h-6 text-enf-accent-fg flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-lg font-bold text-enf-text-primary">Today’s a breath-work day</div>
                          <p className="text-sm text-enf-text-secondary mt-1 leading-relaxed">
                            Between what you’ve flagged, your check-in, and the gear on hand, there’s no loaded
                            movement that fits safely today — so we’re calling a breathing reset. That’s a real
                            session: it settles the nervous system and speeds recovery. Restore the full plan in
                            the notices above any time you want it back.
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {breathDrills.map((ex) => (
                          <button
                            key={ex.id}
                            onClick={() => setPreviewEx(ex)}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-lg text-left transition-colors"
                            data-testid={`breath-drill-${ex.id}`}
                          >
                            <span className="text-sm font-medium text-enf-text-primary">{ex.name}</span>
                            <ChevronRight className="w-4 h-4 text-enf-text-muted flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Workout-level note — single freeform field for whole-session
                    commentary ("not feeling it today", "shoulder twinge", etc).
                    Quiet outline button by default; shows a preview pill once
                    a note exists so the user knows it's there. */}
                <button
                  type="button"
                  onClick={() => setWorkoutNoteOpen(true)}
                  data-testid="workout-note-trigger"
                  className={`w-full mt-3 px-4 py-2.5 flex items-center gap-2 rounded-lg border transition-colors ${
                    workoutNote.trim()
                      ? 'border-enf-role-info/40 bg-enf-role-info/5 text-enf-text-primary hover:bg-enf-role-info/10'
                      : 'border-dashed border-enf-surface-border text-enf-text-muted hover:border-enf-role-info hover:text-enf-role-info'
                  }`}
                >
                  <MessageSquare className="w-4 h-4 flex-shrink-0" />
                  <span className="text-xs font-semibold uppercase tracking-widest flex-shrink-0">
                    {workoutNote.trim() ? 'Workout note' : 'Add workout note'}
                  </span>
                  {workoutNote.trim() && (
                    <span className="ml-1 text-sm text-enf-text-secondary truncate text-left flex-1 normal-case tracking-normal font-normal">
                      {workoutNote.trim()}
                    </span>
                  )}
                </button>

                {/* Complete workout CTA */}
                <button
                  onClick={() => setConfirmFinish(true)}
                  className="w-full mt-4 p-5 bg-gradient-to-r from-enf-surface-special via-enf-border-special to-enf-accent hover:from-enf-border-special hover:via-enf-accent hover:to-enf-accent rounded-xl flex items-center justify-between transition-all group"
                >
                  <div className="text-left">
                    <div className="font-display text-3xl tracking-wide leading-none">Complete today's workout</div>
                    <div className="text-sm text-enf-text-primary/80 mt-1">{stats.doneSets} of {stats.totalSets} sets logged · {displayWeight(stats.totalVolume, units).toLocaleString()} {wUnit} volume</div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center flex-shrink-0 ml-4">
                    <Trophy className="w-6 h-6" />
                  </div>
                </button>
              </>
            )}

            {/* Gear recommendations for no-equipment users.
                forceOpen={bandsCardOpen} is triggered by the band popup CTA,
                which scrolls the user straight into the expanded bands accordion. */}
            {profile.noEquipment && (
              <GearRecommendations groupKey="calisthenics" forceOpen={bandsCardOpen || pullBarCardOpen} />
            )}
          </div>

          {/* Right rail: streak + rest timer + auto-start toggle + coach summary */}
          <div className="min-w-0 space-y-5">
            <StreakWidget />
            <WidgetGate page="strength" id="restTimer">
            <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs uppercase tracking-[0.3em] text-enf-text-muted font-semibold">Rest Timer</div>
                <button
                  onClick={() => setAutoStartRest(!autoStartRest)}
                  className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded border transition-colors ${
                    autoStartRest ? 'bg-enf-accent/10 border-enf-accent-fg/40 text-enf-accent-fg' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-muted hover:text-enf-text-primary'
                  }`}
                  title="Auto-start rest timer when a set is marked done"
                >
                  <Power className="w-3 h-3" /> Auto {autoStartRest ? 'on' : 'off'}
                </button>
              </div>
              <div className="relative w-44 h-44 mx-auto mb-5">
                <svg className="w-full h-full -rotate-90">
                  <circle cx="88" cy="88" r="78" stroke="#27272a" strokeWidth="6" fill="none" />
                  <circle cx="88" cy="88" r="78" stroke="#dc2626" strokeWidth="6" fill="none" strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 78}`} strokeDashoffset={`${2 * Math.PI * 78 * (1 - restPct / 100)}`}
                    className="transition-all duration-1000" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="font-display text-5xl tracking-wide leading-none">{mm}:{ss}</div>
                  <div className="text-xs text-enf-text-muted uppercase tracking-widest mt-1">{restRunning ? 'Resting' : restSeconds > 0 ? 'Paused' : 'Ready'}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setRestRunning((r) => !r)} disabled={restSeconds === 0}
                  className="flex-1 py-2.5 bg-enf-accent hover:bg-enf-accent-hover disabled:bg-enf-surface-mid disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
                  {restRunning ? <><Pause className="w-4 h-4" /> Pause</> : <><Play className="w-4 h-4 fill-white" /> Start</>}
                </button>
                <button onClick={() => { setRestSeconds(0); setRestRunning(false); setRestTarget(0); }}
                  className="px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated rounded-md transition-colors">
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-2 mt-3">
                {[60, 90, 120, 180].map((s) => (
                  <button key={s} onClick={() => startRest(s)} className="flex-1 py-1.5 bg-enf-surface-mid hover:bg-enf-surface-elevated rounded text-xs text-enf-text-secondary font-mono transition-colors">{s}s</button>
                ))}
              </div>
            </div>
            </WidgetGate>

            {/* Coach summary */}
            <WidgetGate page="strength" id="todaysEffort">
            <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-enf-text-muted font-semibold mb-4">Today's effort</div>
              <div className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-enf-text-secondary text-sm">Sets completed</span>
                  <span className="font-display text-2xl tracking-wide">{stats.doneSets}<span className="text-enf-text-muted text-sm">/{stats.totalSets}</span></span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-enf-text-secondary text-sm">Volume lifted</span>
                  <span className="font-display text-2xl tracking-wide text-enf-accent-fg">{displayWeight(stats.totalVolume, units).toLocaleString()}<span className="text-enf-text-muted text-sm ml-1">{wUnit}</span></span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-enf-text-secondary text-sm">Exercises done</span>
                  <span className="font-display text-2xl tracking-wide">{plan.filter((_, i) => (setsByBlock[i] || []).every((s) => s.completed) && (setsByBlock[i] || []).length > 0).length}<span className="text-enf-text-muted text-sm">/{plan.length}</span></span>
                </div>
              </div>
            </div>
            </WidgetGate>
          </div>
        </div>
      </div>

      {/* Sticky floating rest timer — glanceable from anywhere on the page.
          Bottom-center on mobile, top-right on desktop. Hides at 0. The full
          rest-timer card on the right rail stays as the "set duration" control. */}
      {restRunning && restSeconds > 0 && (
        <div
          data-testid="rest-pill"
          className="fixed z-40 bottom-[72px] left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:bottom-auto md:top-24 md:right-6 flex items-center gap-2 px-3 py-2 bg-enf-accent border border-enf-accent-fg rounded-full shadow-2xl shadow-black/60"
        >
          <Flame className="w-4 h-4 scale-x-[-1] text-enf-text-primary" />
          <span className="font-display text-2xl tracking-wide leading-none text-enf-text-primary tabular-nums">{mm}:{ss}</span>
          <button
            onClick={() => setRestRunning((r) => !r)}
            aria-label={restRunning ? 'Pause rest' : 'Resume rest'}
            data-testid="rest-pill-pause"
            className="ml-1 w-8 h-8 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 transition-colors"
          >
            {restRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-white" />}
          </button>
          <button
            onClick={() => setRestSeconds((s) => Math.max(0, s - 10))}
            data-testid="rest-pill-minus"
            className="w-8 h-8 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 text-xs font-bold transition-colors"
          >
            −10
          </button>
          <button
            onClick={() => { setRestSeconds((s) => s + 10); setRestTarget((t) => t + 10); }}
            data-testid="rest-pill-plus"
            className="w-8 h-8 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 text-xs font-bold transition-colors"
          >
            +10
          </button>
          <button
            onClick={() => { setRestSeconds(0); setRestRunning(false); setRestTarget(0); }}
            aria-label="Skip rest"
            data-testid="rest-pill-skip"
            className="w-8 h-8 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Exercise preview dialog — handles both library exercises (rich data) and
          custom exercises (only have name/muscle/notes). Custom exercises render
          a simplified card; library exercises render the full instructions + tips.  */}
      {/* Post-workout celebration — PRs + session-count milestones. Intercepts
          the navigate-to-progress redirect so the user can choose to share before
          moving on. Single "Done" returns to /progress. */}
      {celebration && (
        <CelebrationModal
          celebration={celebration}
          athleteName={profile.socialHandle ? `@${profile.socialHandle.replace(/^@/, '')}` : (profile.name || profile.displayName || '')}
          premium={!!profile.premium}
          units={units}
          onShare={setSharingMilestone}
          onDone={() => {
            setCelebration(null);
            // If a program completion is queued, let it take over instead.
            if (programCompletionPrompt) return;
            // No navigation — user stays on the workout page. Celebrations
            // are the moments worth interrupting for; everything else is a toast.
          }}
        />
      )}
      {/* Program completion confirmation — fires when the user crosses the
          program's target duration. Anti-shame: user confirms or extends. */}
      {!celebration && programCompletionPrompt && (
        <ProgramCompletionDialog
          candidate={programCompletionPrompt}
          onConfirm={() => {
            addProgramCompletion(programCompletionPrompt);
            setProgramCompletionPrompt(null);
            toast.success('Program completed', { description: 'Logged to your Progress timeline.' });
          }}
          onExtend={() => {
            snoozeProgramCompletion();
            setProgramCompletionPrompt(null);
          }}
        />
      )}
      {sharingMilestone && (
        <MilestoneShareCard
          {...sharingMilestone}
          athleteName={profile.socialHandle ? `@${profile.socialHandle.replace(/^@/, '')}` : (profile.name || profile.displayName || '')}
          premium={!!profile.premium}
          onClose={() => setSharingMilestone(null)}
        />
      )}

      <Dialog open={!!previewEx} onOpenChange={(o) => !o && setPreviewEx(null)}>
        <DialogContent className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-w-2xl max-h-[90vh] overflow-y-auto">
          {previewEx && (() => {
            const isCustom = previewEx.isCustom || !Array.isArray(previewEx.instructions);
            const idLabel = previewEx.id != null ? `#${String(previewEx.id).padStart(3, '0')}` : 'CUSTOM';
            return (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-2 py-0.5 bg-enf-accent rounded text-xs font-mono font-bold">{idLabel}</span>
                  <span className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold">
                    {previewEx.muscle || 'Custom'}{previewEx.tier ? ` · Tier ${previewEx.tier}` : ''}
                  </span>
                </div>
                <DialogTitle className="font-display text-4xl tracking-wide leading-none">{previewEx.name}</DialogTitle>
                {previewEx.description && (
                  <DialogDescription className="text-enf-text-secondary">{previewEx.description}</DialogDescription>
                )}
              </DialogHeader>
              {!isCustom && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-4 h-4 text-enf-role-info" />
                    <div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Instructions</div>
                  </div>
                  <ol className="space-y-2">
                    {previewEx.instructions.map((s, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <span className="w-5 h-5 rounded bg-enf-accent flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                        <span className="text-enf-text-primary">{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {isCustom && previewEx.notes && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-4 h-4 text-enf-role-info" />
                    <div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Your notes</div>
                  </div>
                  <p className="text-sm text-enf-text-primary whitespace-pre-wrap">{previewEx.notes}</p>
                </div>
              )}
              {previewEx.tips && (
                <div className="p-3 bg-enf-surface-special/40 border border-enf-border-special/30 rounded-md">
                  <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mb-1">Coach's note</div>
                  <p className="text-sm text-enf-text-primary">{previewEx.tips}</p>
                </div>
              )}
              {isCustom && (
                <div className="text-xs text-enf-text-muted italic">
                  Custom exercise — instructions + coaching cues land in the Library once an admin reviews your suggestion.
                </div>
              )}
              {!isCustom && (
                <Link to={`/exercise/${previewEx.id}?from=/strength`} onClick={() => setPreviewEx(null)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm transition-colors">
                  Open full library entry <ExternalLink className="w-4 h-4" />
                </Link>
              )}
            </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Historical date picker — pick any date in the last 14 days to log a past workout against */}
      <Dialog open={showHistoricalPicker} onOpenChange={setShowHistoricalPicker}>
        <DialogContent className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wide leading-none">Log a past workout</DialogTitle>
            <DialogDescription className="text-enf-text-secondary">
              Pick any date in the last 14 days. We'll preset the template from that day's slot in your schedule — adjust sets and weights to match what you actually did, then finish to log it on the right date.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold block">Workout date</label>
            <input
              type="date"
              min={(() => { const d = new Date(); d.setDate(d.getDate() - 14); return localISO(d); })()}
              max={localISO(new Date())}
              defaultValue={localISO(new Date())}
              onChange={(e) => {
                if (!e.target.value) return;
                // Parse YYYY-MM-DD as local date (avoid UTC drift)
                const [y, m, d] = e.target.value.split('-').map(Number);
                const picked = new Date(y, m - 1, d, 0, 0, 0, 0);
                // Realign selectedDayIdx to day-of-week (Mon=0..Sun=6)
                const dow = (picked.getDay() + 6) % 7;
                setSelectedDayIdx(dow);
                setCustomLogDate(picked);
              }}
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
              data-testid="historical-date-input"
            />
            <p className="text-xs text-enf-text-muted">
              Today and dates in this week are also pickable directly on the day picker above. This is for older sessions.
            </p>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={() => setShowHistoricalPicker(false)} className="flex-1 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold transition-colors">
              Cancel
            </button>
            <button
              onClick={() => setShowHistoricalPicker(false)}
              disabled={!customLogDate}
              className="flex-1 py-2.5 bg-enf-accent hover:bg-enf-accent-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              data-testid="historical-picker-confirm"
            >
              <Check className="w-4 h-4" /> Load template
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Finish confirm */}
      <Dialog open={confirmFinish} onOpenChange={setConfirmFinish}>
        <DialogContent className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wide leading-none">
              {isLoggingHistorical ? `Log for ${selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}?` : 'Finish workout?'}
            </DialogTitle>
            <DialogDescription className="text-enf-text-secondary">
              {isLoggingHistorical && (
                <span className="block mb-2 text-enf-role-info">
                  Logging {daysAgo === 1 ? 'yesterday' : `${daysAgo} days ago`} — your dashboard + pacing will record this on the actual date.
                </span>
              )}
              {stats.doneSets === stats.totalSets && stats.totalSets > 0
                ? (profile.adaptiveMode ? 'Every set logged. Solid work today.' : 'Every set logged. Tempered.')
                : stats.doneSets === 0
                ? `No sets logged yet — want to keep training or log what you've got?`
                : `You logged ${stats.doneSets} of ${stats.totalSets} sets (${displayWeight(stats.totalVolume, units).toLocaleString()} ${wUnit}). That counts.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setConfirmFinish(false)} className="flex-1 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold transition-colors">
              Keep training
            </button>
            <button onClick={handleFinish} data-testid="confirm-finish-button" className="flex-1 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2">
              <Trophy className="w-4 h-4" />
              {isLoggingHistorical ? `Log for ${selectedDate.toLocaleDateString('en-US', { weekday: 'short' })}` : 'Log workout'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Program picker */}
      <Dialog open={showProgramPicker} onOpenChange={setShowProgramPicker}>
        <DialogContent className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wide leading-none">Pick your program</DialogTitle>
            <DialogDescription className="text-enf-text-secondary">
              Switch your weekly split. We'll regenerate your week using your {profile.daysPerWeek || 4} training days.
            </DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            {Object.values(programs)
              .map((p) => {
                const active = programType === p.id;
                const fits = (profile.daysPerWeek || 4) >= p.minDays;
                return (
                  <button key={p.id} onClick={() => {
                    update({ programType: p.id, schedule: null });
                    setShowProgramPicker(false);
                  }}
                    className={`text-left p-4 rounded-xl border transition-all ${
                      active ? 'bg-enf-surface-elevated border-enf-accent-fg' : 'bg-enf-surface-mid border-enf-surface-border hover:border-enf-surface-border hover:bg-enf-surface-elevated'
                    }`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-display text-2xl tracking-wide">{p.name}</div>
                      {active && <Check className="w-4 h-4 text-enf-accent-fg" />}
                    </div>
                    <div className="text-sm text-enf-text-secondary mb-2">{p.description}</div>
                    <div className="text-xs text-enf-text-muted">
                      Best for: {p.bestFor}
                      {!fits && <span className="ml-2 text-enf-role-warning">· needs {p.minDays}+ days/wk</span>}
                    </div>
                  </button>
                );
              })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule editor */}
      <Dialog open={showScheduleEditor} onOpenChange={setShowScheduleEditor}>
        <DialogContent className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wide leading-none">Edit your week</DialogTitle>
            <DialogDescription className="text-enf-text-secondary">
              Drag-free reorder: tap a day, pick what it should be. Endurance days will open the Endurance page when you tap them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {DAYS.map((day, i) => {
              const cur = scheduleDraft[i];
              return (
                <div key={i} className="flex items-center gap-3 p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
                  <div className="w-16 flex-shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">{day.short}</div>
                    <div className="text-sm font-bold text-enf-text-secondary">{day.long.slice(0, 3)}</div>
                  </div>
                  <div className="flex-1 flex flex-wrap gap-1.5">
                    {Object.entries(program.templates).map(([key, tpl]) => (
                      <button key={key} onClick={() => setScheduleDraft((s) => s.map((v, j) => j === i ? key : v))}
                        className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                          cur === key ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border'
                        }`}>
                        {tpl.title.split(' ')[0]}
                      </button>
                    ))}
                    <button onClick={() => setScheduleDraft((s) => s.map((v, j) => j === i ? 'recovery' : v))}
                      className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                        cur === 'recovery' ? 'bg-enf-role-info border-enf-role-info text-enf-accent-contrast' : 'bg-enf-surface-mid border-enf-surface-border text-enf-role-info hover:border-enf-role-info'
                      }`}>
                      Endurance
                    </button>
                    <button onClick={() => setScheduleDraft((s) => s.map((v, j) => j === i ? 'rest' : v))}
                      className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                          cur === 'rest' ? 'bg-enf-surface-elevated border-enf-surface-border text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border'
                      }`}>
                      Rest
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={() => {
              setScheduleDraft(getScheduleForWeek({
                programId: programType,
                experience: profile.experience,
                daysPerWeek: profile.daysPerWeek || 4,
                startDate: profile.startDate || localISO(new Date()),
                hasRecovery,
                viewDate: new Date(),
                baselineOnly: !isPaid,
              }));
            }} className="px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold transition-colors flex items-center gap-2">
              <RotateCcw className="w-4 h-4" /> Reset to default
            </button>
            <div className="flex-1" />
            <button onClick={() => setShowScheduleEditor(false)}
              className="px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold transition-colors">
              Cancel
            </button>
            <button onClick={() => {
              update({ schedule: scheduleDraft });
              setShowScheduleEditor(false);
            }} className="px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors flex items-center gap-2">
              <Check className="w-4 h-4" /> Save week
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI workout generator — uses current day's focus as the seed */}
      <AIWorkoutModal
        open={showAiWorkout}
        onClose={() => setShowAiWorkout(false)}
        focus={todayTemplate?.title || 'Full Body'}
        defaultDayType={defaultDayType}
        excludeIds={plan.map((b) => b?.ex?.id).filter(Boolean)}
        onApply={handleApplyAiWorkout}
      />

      <PaywallModal open={aiPaywallOpen} onOpenChange={setAiPaywallOpen} feature="ai_workouts" />

      <AddExerciseSheet
        open={showAddExercise}
        onClose={() => { setShowAddExercise(false); setReplaceTarget(null); }}
        onSelect={handleExerciseSelected}
        mode={replaceTarget !== null ? 'replace' : 'add'}
        targetExerciseName={replaceTarget !== null ? (plan[replaceTarget]?.ex?.name ?? '') : ''}
        baselineOnly={!isPaid}
      />

      <EditProfileModal
        section={showEquipmentEdit ? 'equipment' : null}
        open={showEquipmentEdit}
        onClose={() => setShowEquipmentEdit(false)}
      />

      {/* Edit logged workout — opened from the already-logged gate */}
      <StrengthEntryEditModal
        entry={editingLoggedEntry}
        open={!!editingLoggedEntry}
        onClose={() => setEditingLoggedEntry(null)}
        onSave={(patch) => editingLoggedEntry && updateWorkout(editingLoggedEntry.id, patch)}
        onDelete={() => editingLoggedEntry && removeWorkout(editingLoggedEntry.id)}
      />


      {/* Workout-level note Dialog */}
      <Dialog open={workoutNoteOpen} onOpenChange={setWorkoutNoteOpen}>
        <DialogContent className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wide leading-none flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-enf-role-info" /> Workout note
            </DialogTitle>
            <DialogDescription className="text-enf-text-secondary text-sm mt-1">
              Anything about today's session worth remembering. Lifts off the exercise list
              and onto the day itself.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={workoutNote}
            onChange={(ev) => setWorkoutNote(ev.target.value)}
            rows={5}
            placeholder={`Not really feeling it today.\nSmall twinge in shoulder on some lifts I should keep in mind.\nGym was packed, swapped a few benches for dumbbell variants.`}
            data-testid="workout-note-textarea"
            className="w-full mt-2 px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none placeholder:text-enf-text-muted whitespace-pre-line"
          />
          <div className="flex gap-2 mt-3">
            {workoutNote.trim() && (
              <button
                onClick={() => { setWorkoutNote(''); setWorkoutNoteOpen(false); }}
                className="px-3 py-2 text-sm text-enf-role-danger hover:bg-enf-role-danger/10 rounded-md transition-colors"
                data-testid="workout-note-clear"
              >
                Clear
              </button>
            )}
            <button
              onClick={() => setWorkoutNoteOpen(false)}
              className="ml-auto px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover text-enf-accent-contrast rounded-md text-sm font-semibold transition-colors"
              data-testid="workout-note-save"
            >
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Day-already-logged gate ----------
// Renders when the selected day already has at least one logged workout. Lists
// what's logged, with explicit CTAs for editing or starting a second session.
// Replaces the prior behavior of silently re-showing blank exercise cards.
const DayAlreadyLoggedGate = ({ workouts, selectedDate, units, onEdit, onLogAnother }) => {
  const wUnit = weightUnit(units);
  const dateLabel = selectedDate.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  return (
    <div className="bg-enf-surface-deep border border-enf-accent-fg/40 rounded-xl p-6" data-testid="day-already-logged-gate">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-enf-accent/20 border border-enf-accent-fg/30 flex items-center justify-center flex-shrink-0">
          <Check className="w-5 h-5 text-enf-accent-fg" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-enf-accent-fg font-semibold mb-0.5">Workout logged</div>
          <h3 className="font-display text-3xl tracking-wide leading-none truncate">{dateLabel}</h3>
        </div>
      </div>

      <p className="text-sm text-enf-text-secondary mb-4 leading-snug">
        You've already closed out training for this day. Edit what's logged below, or start a second session if you trained twice.
      </p>

      <div className="space-y-2 mb-5">
        {workouts.map((w) => (
          <button
            key={w.id}
            onClick={() => onEdit(w)}
            data-testid={`logged-workout-card-${w.id}`}
            className="w-full text-left p-3 bg-enf-surface-mid border border-enf-surface-border hover:border-enf-role-info rounded-lg transition-colors group"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-display text-xl tracking-wide leading-none mb-1">{w.title || 'Workout'}</div>
                <div className="flex flex-wrap gap-3 text-xs text-enf-text-secondary">
                  <span>{w.exercisesCompleted ?? 0}/{w.exerciseCount ?? 0} exercises</span>
                  <span>{(w.totalVolume ?? 0).toLocaleString()} {wUnit} volume</span>
                  <span>{w.duration ?? 0} min</span>
                </div>
                {w.notes && (
                  <div className="mt-2 text-xs text-enf-text-muted italic line-clamp-2">"{w.notes}"</div>
                )}
              </div>
              <Edit3 className="w-4 h-4 text-enf-text-muted group-hover:text-enf-role-info flex-shrink-0 mt-1" />
            </div>
          </button>
        ))}
      </div>

      <button
        onClick={onLogAnother}
        data-testid="log-another-workout"
        className="w-full px-4 py-3 bg-enf-surface-mid hover:bg-enf-accent/20 border border-dashed border-enf-surface-border hover:border-enf-accent-fg rounded-lg text-sm font-semibold text-enf-text-secondary hover:text-enf-text-primary transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" />
        Log another workout for {dateLabel.split(',')[0]}
      </button>
    </div>
  );
};

// ---------- Circuit Exercise Row (simplified card for circuit/bodyweight sessions) ----------
// Shows exercise name, target muscle, round completion circles, and a per-round
// "done" button. No weight inputs — circuit mode is bodyweight.

const CircuitExerciseRow = ({
  blockIdx, block, sets, currentRound, circuitRounds,
  skipped, exerciseDone,
  onToggleSkip, onPreview, onMarkSetDone, onSwap,
}) => {
  const e = block.ex;
  const currentRoundSet = sets[currentRound - 1];
  const currentRoundDone = !!currentRoundSet?.completed;

  return (
    <div className={`bg-enf-surface-deep border rounded-xl transition-all overflow-hidden mb-3 ${
      exerciseDone ? 'border-enf-role-success/40 opacity-70' : 'border-enf-surface-border'
    } ${skipped ? 'opacity-40' : ''}`}>
      <div className="p-4 flex items-start gap-3">
        {/* Exercise ID chip */}
        <button
          onClick={onPreview}
          className="font-mono text-[11px] px-2 py-0.5 rounded bg-enf-surface-mid hover:bg-enf-surface-special text-enf-text-secondary hover:text-enf-role-info border border-enf-surface-border hover:border-enf-role-info/50 transition-colors flex-shrink-0 mt-0.5"
          title="View exercise details"
          data-testid={`exercise-index-${blockIdx}`}
        >
          #{String(e.id).padStart(3, '0')}
        </button>

        {/* Name + muscle + rep target */}
        <div className="flex-1 min-w-0">
          <div className="font-display text-base leading-snug break-words text-enf-text-primary">{e.name}</div>
          <div className="text-xs text-enf-text-muted mt-0.5">{e.muscle}</div>
          {block.reps && (
            <div className="text-xs text-enf-accent-fg/80 font-medium mt-0.5">{block.reps} reps per round</div>
          )}
        </div>

        {/* Round completion dots */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {Array.from({ length: circuitRounds }, (_, i) => {
            const done = sets[i]?.completed;
            const active = i === currentRound - 1 && !done;
            return (
              <div
                key={i}
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 transition-colors ${
                  done ? 'bg-enf-role-success border-enf-role-success text-white'
                    : active ? 'border-enf-accent-fg text-enf-accent-fg bg-transparent'
                    : 'border-enf-surface-border text-enf-text-muted bg-transparent'
                }`}
              >
                {done ? <Check className="w-3 h-3" /> : i + 1}
              </div>
            );
          })}
        </div>
      </div>

      {/* Action row */}
      {!exerciseDone && (
        <div className="px-4 pb-4 flex items-center gap-2">
          {!skipped && !currentRoundDone && currentRoundSet && (
            <button
              onClick={() => onMarkSetDone(currentRoundSet.id)}
              className="flex-1 py-2.5 bg-enf-accent/20 hover:bg-enf-accent border border-enf-accent-fg/40 hover:border-enf-accent-fg rounded-lg text-sm font-semibold text-enf-accent-fg hover:text-enf-text-primary transition-colors"
              data-testid={`circuit-done-${blockIdx}`}
            >
              Round {currentRound} done ✓
            </button>
          )}
          {!skipped && currentRoundDone && !exerciseDone && (
            <div className="flex-1 py-2.5 text-center text-sm font-semibold text-enf-role-success">
              ✓ Round {currentRound} logged
            </div>
          )}
          {!skipped && (
            <button
              onClick={onSwap}
              className="px-3 py-2.5 rounded-lg border text-xs font-semibold transition-colors bg-transparent border-enf-surface-border text-enf-text-muted hover:text-enf-role-info hover:border-enf-role-info/50"
              data-testid={`swap-exercise-${blockIdx}`}
            >
              Swap
            </button>
          )}
          <button
            onClick={onToggleSkip}
            className={`px-3 py-2.5 rounded-lg border text-xs font-semibold transition-colors ${
              skipped
                ? 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                : 'bg-transparent border-enf-surface-border text-enf-text-muted hover:text-enf-role-warning hover:border-enf-role-warning/50'
            }`}
            data-testid={`skip-exercise-${blockIdx}`}
          >
            {skipped ? 'Undo' : 'Skip'}
          </button>
        </div>
      )}
    </div>
  );
};

// ---------- Exercise Row (inline-expandable with per-set grid) ----------

const ExerciseRow = ({
  blockIdx, block, sets, expanded, exerciseDone,
  skipped, onToggleSkip, onSwap,
  onToggleExpand, onPreview,
  onUpdateSet, onMarkSetDone, onAddSet, onAddDropset, onRemoveSet,
  onCopyLastSession,
  note, onChangeNote,
  lastTime, lastTimeStr, units,
  level,
}) => {
  const e = block.ex;
  const doneCount = sets.filter((s) => s.completed).length;
  const firstHist = lastTime?.sets?.[0];
  const hasNote = !!(note && note.trim());
  const [noteOpen, setNoteOpen] = useState(hasNote);

  return (
    <div className={`bg-enf-surface-deep border rounded-xl transition-all overflow-hidden ${
      expanded ? 'border-enf-accent-fg' : 'border-enf-surface-border hover:border-enf-surface-border'
    } ${exerciseDone ? 'opacity-70' : ''} ${skipped ? 'opacity-50' : ''}`}>
      {/* Header — two-row layout (restructure).
          Row 1: utility strip — index, sets-done counter, skip, expand chevron.
          Row 2: exercise name (wraps if long) + meta chips.
          Why two rows: when the row was packed onto one line, names got
          truncated on mobile and the Skip control had to ship as icon-only
          (confusing to new users). Splitting frees full card width for the
          name (with `break-words` instead of `truncate`) and lets every
          control carry its real label at every breakpoint. */}
      <div className="p-4">
        {/* Row 1 — utility strip */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={onPreview}
              className="font-mono text-[11px] px-2 py-0.5 rounded bg-enf-surface-mid hover:bg-enf-surface-special text-enf-text-secondary hover:text-enf-role-info border border-enf-surface-border hover:border-enf-role-info/50 transition-colors flex-shrink-0"
              title="View exercise details"
              data-testid={`exercise-index-${blockIdx}`}
            >
              #{String(e.id).padStart(3, '0')}
            </button>
            {!skipped && (
              <div className="text-xs font-mono text-enf-text-secondary flex items-center gap-1 flex-shrink-0" data-testid={`sets-done-${blockIdx}`}>
                <span className="uppercase tracking-widest text-[10px] text-enf-text-muted">Sets done</span>
                <span>{doneCount}/{sets.length}</span>
              </div>
            )}
            {skipped && (
              <span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold flex-shrink-0">Skipped</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!skipped && (
              <button
                onClick={onSwap}
                className="text-xs px-2.5 py-1 rounded border font-semibold transition-colors bg-transparent border-enf-surface-border text-enf-text-muted hover:text-enf-role-info hover:border-enf-role-info/50"
                title="Replace this exercise with a different one"
                data-testid={`swap-exercise-${blockIdx}`}
                aria-label="Swap exercise"
              >
                Swap
              </button>
            )}
            <button
              onClick={onToggleSkip}
              className={`text-xs px-2.5 py-1 rounded border font-semibold transition-colors ${
                skipped
                  ? 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                  : 'bg-transparent border-enf-surface-border text-enf-text-muted hover:text-enf-role-warning hover:border-enf-role-warning/50'
              }`}
              title={skipped ? 'Restore this exercise' : 'Skip this exercise — keep in list for awareness, exclude from totals'}
              data-testid={`skip-exercise-${blockIdx}`}
              aria-label={skipped ? 'Restore exercise' : 'Skip exercise'}
            >
              {skipped ? 'Undo' : 'Skip'}
            </button>
            {!skipped && (
              <button
                onClick={onToggleExpand}
                className="text-enf-text-muted hover:text-enf-text-primary p-1 -m-1"
                aria-label={expanded ? 'Collapse exercise' : 'Expand exercise'}
                data-testid={`exercise-expand-${blockIdx}`}
              >
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>

        {/* Row 2 — name + meta. The whole row is the expand tap target on
            mobile; the smaller chevron stays as the desktop affordance. */}
        <button
          onClick={skipped ? undefined : onToggleExpand}
          disabled={skipped}
          className="w-full text-left disabled:cursor-default"
          data-testid={`exercise-row-${blockIdx}`}
        >
          <div className={`font-bold text-base leading-snug break-words ${skipped ? 'line-through text-enf-text-muted' : ''}`}>
            {e.name}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-enf-text-muted mt-1">
            <span>{e.muscle} · Tier {e.tier}</span>
            {!skipped && (() => {
              const coreLabel = getCoreRepsLabel(e, level);
              if (coreLabel) {
                return (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-enf-surface-mid border border-enf-surface-border font-mono text-enf-text-secondary text-[10px]">
                    {e.isometric ? coreLabel : `Target ${coreLabel}`}
                  </span>
                );
              }
              if (block.reps) {
                return (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-enf-surface-mid border border-enf-surface-border font-mono text-enf-text-secondary text-[10px]">
                    Target {block.reps} Reps
                  </span>
                );
              }
              return null;
            })()}
            {block.substituted && !skipped && (
              block.beneficialPick
                ? <span className="inline-flex items-center gap-1 text-emerald-500" title={`Joint-friendly swap for ${block.originalName}`}><HeartPulse className="w-3 h-3" /> Joint-friendly sub for {block.originalName}</span>
                : <span className="inline-flex items-center gap-1 text-enf-role-warning"><AlertCircle className="w-3 h-3" /> Sub for {block.originalName}</span>
            )}
          </div>
        </button>
      </div>

      {/* Expanded set grid */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-enf-surface-border">
          {/* "Last time" pill removed — values now surface via
              per-set "↻ Last" quick-fill chips on the row itself, so the user
              isn't anchored by a passive read-only summary. Bulk-fill is one
              tap away via the header-level "Copy last session" button. */}

          {/* "Last note" hint — surfaces the per-exercise note from the user's
              previous session against this lift. This is the payoff of
              attaching notes to specific exercises (vs. only the whole workout). */}
          {lastTime?.note && (
            <div className="mt-3 mb-3 px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md flex items-start gap-2" data-testid={`last-note-${blockIdx}`}>
              <MessageSquare className="w-3.5 h-3.5 text-enf-text-muted flex-shrink-0 mt-0.5" />
              <div className="flex flex-col min-w-0">
                <span className="text-[10px] uppercase tracking-[0.2em] text-enf-text-muted font-semibold leading-none mb-1">Last note</span>
                <span className="text-sm text-enf-text-secondary leading-snug">{lastTime.note}</span>
              </div>
            </div>
          )}

          {/* Header-level "Copy last session" — single tap fills every set with
              last week's matching values. Explicit, opt-in convenience for
              steady-state users without imposing it on everyone. */}
          {lastTime && lastTime.sets && lastTime.sets.length > 0 && (
            <div className="mb-3">
              <button
                type="button"
                onClick={onCopyLastSession}
                data-testid={`copy-last-session-${blockIdx}`}
                className="w-full px-3 py-2 text-xs font-semibold tracking-widest uppercase bg-enf-role-info/10 hover:bg-enf-role-info/20 text-enf-role-info border border-enf-role-info/30 rounded-md transition-colors flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Copy last session
              </button>
            </div>
          )}

          {/* Column headers — flex proportions mirror the SetRow cells so
              headers stay aligned in the common single-line case. */}
          <div className="flex gap-2 text-xs text-enf-text-muted uppercase tracking-widest font-semibold mb-2 px-1">
            <div className="shrink-0 basis-[1.75rem]">Set</div>
            <div className="grow-[4] basis-0">Weight</div>
            <div className="grow-[4] basis-0">Reps</div>
            <div className="grow-[3] basis-0 text-right">Status</div>
          </div>

          {/* Set rows */}
          <div className="space-y-1.5">
            {sets.map((s, idx) => {
              const isChild = !!s.parentId;
              const setLabel = isChild ? 'D' : String(sets.filter((x, j) => !x.parentId && j <= idx).length);
              // Per-set historical pairing: set N matches sets[N] last time, falling
              // back to sets[0] if last session had fewer sets. Drop sets skip this
              // (they're a today-only flourish, no historical analogue).
              const histIdx = sets.slice(0, idx + 1).filter((x) => !x.parentId).length - 1;
              const lastSet = isChild ? null : (lastTime?.sets?.[histIdx] || firstHist);
              return (
                <SetRow
                  key={s.id}
                  set={s}
                  setLabel={setLabel}
                  isChild={isChild}
                  lastSet={lastSet}
                  units={units}
                  block={block}
                  onChange={(patch) => onUpdateSet(s.id, patch)}
                  onToggleDone={() => onMarkSetDone(s.id)}
                  onAddDropset={() => onAddDropset(s.id)}
                  onRemove={() => onRemoveSet(s.id)}
                />
              );
            })}
          </div>

          {/* Add set */}
          <button
            onClick={onAddSet}
            className="w-full mt-3 py-2 border border-dashed border-enf-surface-border hover:border-enf-role-info text-sm text-enf-text-muted hover:text-enf-role-info rounded-md transition-colors flex items-center justify-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add set
          </button>

          {/* Per-exercise note — collapsed by default; expands inline.
              Use case: "2x warmup sets first", "drop set on the last set",
              technique notes that surface against this lift next time. */}
          {!noteOpen ? (
            <button
              onClick={() => setNoteOpen(true)}
              data-testid={`exercise-note-add-${blockIdx}`}
              className="w-full mt-2 py-1.5 text-xs text-enf-text-muted hover:text-enf-role-info flex items-center justify-center gap-1.5 transition-colors"
            >
              <Edit3 className="w-3 h-3" /> Add note
            </button>
          ) : (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Note for this exercise</span>
                {!hasNote && (
                  <button
                    onClick={() => { setNoteOpen(false); onChangeNote(''); }}
                    className="text-[10px] text-enf-text-muted hover:text-enf-text-primary"
                    data-testid={`exercise-note-close-${blockIdx}`}
                  >
                    Hide
                  </button>
                )}
              </div>
              <textarea
                value={note}
                onChange={(ev) => onChangeNote(ev.target.value)}
                rows={2}
                placeholder="e.g. 2 warmup sets first · drop set on the last · sore shoulder"
                data-testid={`exercise-note-textarea-${blockIdx}`}
                className="w-full px-2 py-1.5 bg-enf-surface-mid border border-enf-surface-border rounded text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none placeholder:text-enf-text-muted"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------- Set Row ----------

const SetRow = ({ set, setLabel, isChild, lastSet, units = 'imperial', block, onChange, onToggleDone, onAddDropset, onRemove }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const done = set.completed;
  const wUnit = weightUnit(units);
  const isIsometric = !!block.ex?.isometric;
  // Show "↻ Last: 135 × 8" chip whenever history exists for this set's index
  // AND the user hasn't already typed values matching it. Auto-prefill was
  // removed to avoid anchoring the user to last week's ceiling —
  // the chip is the explicit, opt-in alternative.
  const hasLast = !!lastSet && !done && !isChild;
  const inputsMatchLast = hasLast &&
    (isIsometric
      ? String(set.reps) === String(lastSet.reps)
      : Number(set.weight) === Number(lastSet.weight) && String(set.reps) === String(lastSet.reps));
  const canRepeat = hasLast && !inputsMatchLast;
  // Reps may be a rep range like "8-10" or AMRAP-ish string — don't show steppers
  // in those cases since +/- wouldn't have a clear meaning. Numeric-only reps
  // (the common case) get stepper controls.
  const repsIsNumeric = /^\d+$/.test(String(set.reps || '').trim());
  const weightNum = Number(set.weight) || 0;
  const repsNum = repsIsNumeric ? Number(set.reps) : 0;
  // Step in canonical lb internally, but the user sees increments in their
  // preferred unit (5 lb or 2.5 kg).
  const stepLb = weightStepLb(units);
  const stepWeight = (sign) => onChange({ weight: Math.max(0, weightNum + sign * stepLb) });
  // Isometric: step seconds in 5s increments. Dynamic: step reps by 1.
  const stepReps = (delta) => onChange({ reps: String(Math.max(1, repsNum + (isIsometric ? delta * 5 : delta))) });
  // Display the canonical lb weight in the user's preferred unit
  const displayedWeight = weightNum > 0 ? displayWeight(weightNum, units) : '';

  return (
    <div className={`relative ${isChild ? 'pl-6' : ''}`}>
      {isChild && (
        <span className="absolute left-2 top-0 bottom-0 w-px bg-enf-accent/40" />
      )}
      {isChild && (
        <span className="absolute left-2 top-1/2 w-3 h-px bg-enf-accent/40" />
      )}
      <div className={`flex flex-wrap gap-2 items-center px-2 py-1.5 rounded-md ${
        done ? 'bg-enf-accent/5 border border-enf-accent-fg/30' : 'bg-enf-surface-mid border border-enf-surface-border'
      } ${isChild ? 'bg-enf-surface-special/20' : ''}`}>
        <div className="shrink-0 basis-[1.75rem] flex items-center gap-1">
          <span className={`font-mono text-sm font-bold ${isChild ? 'text-enf-accent-fg' : 'text-enf-text-secondary'}`}>{setLabel}</span>
          {isChild && <span className="text-[9px] uppercase tracking-wider text-enf-accent-fg font-bold">drop</span>}
        </div>
        {/* Weight field — hidden for isometric holds (bodyweight, no load) */}
        {!isIsometric && (
          <div className="grow-[4] basis-0 min-w-[6rem]">
            <div className="flex items-stretch gap-0.5">
              <button
                type="button"
                onClick={() => stepWeight(-1)}
                disabled={done || weightNum === 0}
                aria-label={`Decrease weight by ${units === 'metric' ? '2.5 kg' : '5 lb'}`}
                data-testid="weight-minus"
                className="w-7 flex-shrink-0 flex items-center justify-center bg-enf-surface-mid hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent-fg rounded-l text-enf-text-secondary hover:text-enf-text-primary text-sm font-bold disabled:opacity-40 disabled:hover:bg-enf-surface-mid disabled:hover:border-enf-surface-border transition-colors"
              >−</button>
              <input
                type="number"
                inputMode="decimal"
                data-weight-input-id={set.id}
                value={displayedWeight}
                placeholder={wUnit}
                onChange={(e) => onChange({ weight: parseWeightInput(e.target.value, units) })}
                disabled={done}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                className="flex-1 min-w-[2.5rem] px-1 py-1.5 bg-enf-surface-mid border-y border-enf-surface-border text-enf-text-primary text-sm text-center focus:outline-none focus:border-enf-accent-fg focus:bg-enf-surface-elevated disabled:opacity-60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button
                type="button"
                onClick={() => stepWeight(1)}
                disabled={done}
                aria-label={`Increase weight by ${units === 'metric' ? '2.5 kg' : '5 lb'}`}
                data-testid="weight-plus"
                className="w-7 flex-shrink-0 flex items-center justify-center bg-enf-surface-mid hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent-fg rounded-r text-enf-text-secondary hover:text-enf-text-primary text-sm font-bold disabled:opacity-40 disabled:hover:bg-enf-surface-mid disabled:hover:border-enf-surface-border transition-colors"
              >+</button>
            </div>
          </div>
        )}
        <div className={`basis-0 min-w-[5.5rem] ${isIsometric ? 'grow-[8]' : 'grow-[4]'}`}>
          {/* Reps input — shows "sec" placeholder for isometric holds; steps by 5s. */}
          <div className="flex items-stretch gap-0.5">
            <button
              type="button"
              onClick={() => stepReps(-1)}
              disabled={done || repsNum <= (isIsometric ? 5 : 1)}
              aria-label={isIsometric ? 'Decrease seconds' : 'Decrease reps'}
              data-testid="reps-minus"
              className="w-7 flex-shrink-0 flex items-center justify-center bg-enf-surface-mid hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent-fg rounded-l text-enf-text-secondary hover:text-enf-text-primary text-sm font-bold disabled:opacity-40 disabled:hover:bg-enf-surface-mid disabled:hover:border-enf-surface-border transition-colors"
            >−</button>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              min={isIsometric ? '5' : '1'}
              step={isIsometric ? '5' : '1'}
              value={set.reps}
              onChange={(e) => onChange({ reps: e.target.value })}
              disabled={done}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              placeholder={isIsometric ? 'sec' : ''}
              className="flex-1 min-w-[2.5rem] px-1 py-1.5 bg-enf-surface-mid border-y border-enf-surface-border text-enf-text-primary text-sm text-center focus:outline-none focus:border-enf-accent-fg focus:bg-enf-surface-mid disabled:opacity-60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <button
              type="button"
              onClick={() => stepReps(1)}
              disabled={done}
              aria-label={isIsometric ? 'Increase seconds' : 'Increase reps'}
              data-testid="reps-plus"
              className="w-7 flex-shrink-0 flex items-center justify-center bg-enf-surface-mid hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent-fg rounded-r text-enf-text-secondary hover:text-enf-text-primary text-sm font-bold disabled:opacity-40 disabled:hover:bg-enf-surface-mid disabled:hover:border-enf-surface-border transition-colors"
            >+</button>
          </div>
          {isIsometric && (
            <div className="text-[10px] text-center text-enf-text-muted mt-0.5 font-mono">seconds</div>
          )}
        </div>
        <div className="grow-[3] basis-0 min-w-[6rem] flex items-center justify-end gap-1">
          {canRepeat && (
            <button
              onClick={() => onChange(isIsometric
                ? { reps: lastSet.reps }
                : { weight: lastSet.weight, reps: lastSet.reps }
              )}
              title={isIsometric
                ? `Last: ${lastSet.reps}s hold`
                : `Copy last session: ${displayWeight(lastSet.weight, units)} ${wUnit} × ${lastSet.reps}`}
              data-testid="repeat-last-button"
              aria-label="Copy last session's values for this set"
              className="hidden sm:flex items-center gap-1 px-1.5 h-7 rounded bg-enf-role-info/10 hover:bg-enf-role-info/20 text-enf-role-info border border-enf-role-info/30 hover:border-enf-role-info transition-colors text-[10px] font-mono whitespace-nowrap"
            >
              <RotateCcw className="w-2.5 h-2.5" />
              <span>{isIsometric ? `${lastSet.reps}s` : `${displayWeight(lastSet.weight, units)}×${lastSet.reps}`}</span>
            </button>
          )}
          {/* Mobile per-set quick-fill icon removed: the col-span-3
              status cell on mobile is ~64px, which fits `Log + ⋯` only. The
              extra ↻ icon was pushing the Log button off the right edge of
              the card (clipped by overflow-hidden). Mobile users still get
              bulk-fill via the header-level "Copy last session" button. */}
          <button
            onClick={onToggleDone}
            className={`flex-1 py-1.5 rounded text-xs font-bold uppercase tracking-wider transition-colors ${
              done ? 'bg-enf-accent text-enf-text-primary hover:bg-enf-accent' : 'bg-enf-surface-elevated hover:bg-enf-accent text-enf-text-secondary hover:text-enf-text-primary'
            }`}
          >
            {done ? <Check className="w-3.5 h-3.5 mx-auto" /> : 'Log'}
          </button>
          <div className="relative">
            <button onClick={() => setMenuOpen(!menuOpen)} className="w-7 h-7 rounded bg-enf-surface-mid hover:bg-enf-surface-elevated text-enf-text-muted hover:text-enf-text-primary flex items-center justify-center transition-colors">
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-8 z-20 w-40 bg-enf-surface-deep border border-enf-surface-border rounded-md shadow-lg py-1">
                  {!isChild && (
                    <button onClick={() => { onAddDropset(); setMenuOpen(false); }}
                      className="w-full px-3 py-1.5 text-left text-xs text-enf-text-secondary hover:bg-enf-accent hover:text-enf-text-primary transition-colors flex items-center gap-2">
                      <Plus className="w-3 h-3" /> Add drop set
                    </button>
                  )}
                  <button onClick={() => { onRemove(); setMenuOpen(false); }}
                    className="w-full px-3 py-1.5 text-left text-xs text-enf-role-warning hover:bg-enf-accent hover:text-enf-text-primary transition-colors flex items-center gap-2">
                    <X className="w-3 h-3" /> Remove set
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Inline drop-set link — surfaces the common power-lifter action after a
          working set lands, instead of burying it in the ⋯ menu. */}
      {done && !isChild && (
        <button
          type="button"
          onClick={onAddDropset}
          data-testid="inline-drop-set"
          className="ml-8 mt-1 mb-1 inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-wider text-enf-accent-fg/70 hover:text-enf-accent-fg transition-colors"
        >
          <Plus className="w-3 h-3" /> drop set
        </button>
      )}
    </div>
  );
};

// ---------- Celebration Modal ----------
// Shown after a workout when at least one PR was hit or a session-count
// threshold (10/25/50/100/250/500/1000) was crossed. Surfaces each milestone
// as an inline card with a "Share" button that opens MilestoneShareCard.
const CelebrationModal = ({ celebration, athleteName, premium, onShare, onDone, units = 'imperial' }) => {
  const { prs, sessionMilestone, streakMilestone } = celebration;
  const hasAny = (prs && prs.length > 0) || !!sessionMilestone || !!streakMilestone;
  if (!hasAny) return null;

  const wUnit = weightUnit(units);
  const dispW = (lb) => displayWeight(lb, units);

  const buildPrMilestone = (pr) => ({
    eyebrow: `NEW PR · ${pr.exerciseName.toUpperCase()}`,
    headline: 'HEAVIER THAN YESTERDAY',
    bigValue: `${dispW(pr.weight)} × ${pr.reps}`,
    subline: pr.prevBestE1RM > 0
      ? `Est. 1RM ${dispW(pr.todayE1RM)} ${wUnit} · +${dispW(pr.todayE1RM - pr.prevBestE1RM)} from previous best`
      : `Est. 1RM ${dispW(pr.todayE1RM)} ${wUnit}`,
    accent: '#ef4444',
    taglineContext: 'pr',
    shareTitle: `New PR on ${pr.exerciseName}`,
    shareText: `New PR on ${pr.exerciseName}: ${dispW(pr.weight)} ${wUnit} × ${pr.reps}. Bring the heat. enferno.app`,
    filenameBase: `enferno-pr-${pr.exerciseName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
  });

  const buildSessionMilestone = (n) => ({
    eyebrow: `WORKOUT MILESTONE`,
    headline: `${n} SESSIONS LOGGED`,
    bigValue: `${n}`,
    subline: 'Showing up is the work.',
    accent: '#0ea5e9',
    taglineContext: 'sessions',
    shareTitle: `${n} workouts logged on Enferno`,
    shareText: `${n} workouts logged. Bring the heat. enferno.app`,
    filenameBase: `enferno-${n}-sessions`,
  });

  const buildStreakMilestone = (ms) => ({
    eyebrow: `${ms.value} ${ms.unit.toUpperCase()} ADHERENCE`,
    headline: 'PERPETUAL FLAME',
    bigValue: `${ms.value}${ms.unit === 'days' ? 'D' : 'W'}`,
    subline: 'Stayed the course.',
    accent: '#dc2626',
    taglineContext: 'streak',
    shareTitle: `${ms.value} ${ms.unit} streak on Enferno`,
    shareText: `${ms.value} ${ms.unit} of adherence. Perpetual flame. enferno.app`,
    filenameBase: `enferno-streak-${ms.value}${ms.unit}`,
  });

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/85" data-testid="celebration-modal">
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-enf-surface-deep border border-enf-accent-fg/40 rounded-xl p-6" style={{ boxShadow: '0 0 0 1px rgba(239,68,68,0.2), 0 30px 60px rgba(0,0,0,0.6)' }}>
        <div className="text-center mb-5">
          <div className="text-xs uppercase tracking-widest text-enf-accent-fg font-semibold mb-2 flex items-center justify-center gap-1.5">
            <Award className="w-4 h-4" /> Today landed something
          </div>
          <div className="font-display text-3xl tracking-wide leading-tight">Mark it before you move on.</div>
        </div>

        <div className="space-y-3">
          {streakMilestone && (
            <div className="p-4 bg-enf-accent/5 border border-enf-accent-fg/40 rounded-md" data-testid="celebration-streak-milestone" style={{ boxShadow: '0 0 24px -8px rgba(239,68,68,0.4)' }}>
              <div className="text-[10px] uppercase tracking-widest text-enf-accent-fg font-semibold mb-1">Streak milestone · Perpetual Flame</div>
              <div className="font-display text-2xl mb-2">{streakMilestone.value} {streakMilestone.unit} of adherence</div>
              <button onClick={() => onShare(buildStreakMilestone(streakMilestone))}
                data-testid="celebration-streak-share"
                className="w-full py-2 bg-enf-accent/10 hover:bg-enf-accent text-enf-accent-fg hover:text-enf-text-primary border border-enf-accent-fg/40 hover:border-enf-accent-fg rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
                <Share2 className="w-3.5 h-3.5" /> Share streak
              </button>
            </div>
          )}
          {sessionMilestone && (
            <div className="p-4 bg-enf-role-info/5 border border-enf-role-info/30 rounded-md" data-testid="celebration-session-milestone">
              <div className="text-[10px] uppercase tracking-widest text-enf-role-info font-semibold mb-1">Session milestone</div>
              <div className="font-display text-2xl mb-2">{sessionMilestone} sessions logged</div>
              <button onClick={() => onShare(buildSessionMilestone(sessionMilestone))}
                data-testid="celebration-session-share"
                className="w-full py-2 bg-enf-role-info/10 hover:bg-enf-role-info hover:text-enf-accent-contrast text-enf-role-info border border-enf-role-info/40 hover:border-enf-role-info rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
                <Share2 className="w-3.5 h-3.5" /> Share milestone
              </button>
            </div>
          )}
          {(prs || []).map((pr, idx) => (
            <div key={pr.refId} className="p-4 bg-enf-accent/5 border border-enf-accent-fg/30 rounded-md" data-testid={`celebration-pr-${idx}`}>
              <div className="text-[10px] uppercase tracking-widest text-enf-accent-fg font-semibold mb-1">New PR</div>
              <div className="font-display text-xl mb-0.5">{pr.exerciseName}</div>
              <div className="text-sm text-enf-text-secondary mb-2 font-mono">
                {dispW(pr.weight)} {wUnit} × {pr.reps}{' '}
                <span className="text-enf-text-muted">· est. 1RM {dispW(pr.todayE1RM)} {wUnit}</span>
                {pr.prevBestE1RM > 0 && (
                  <span className="text-enf-accent-fg"> · +{dispW(pr.todayE1RM - pr.prevBestE1RM)} from prev best</span>
                )}
              </div>
              <button onClick={() => onShare(buildPrMilestone(pr))}
                data-testid={`celebration-pr-share-${idx}`}
                className="w-full py-2 bg-enf-accent/10 hover:bg-enf-accent text-enf-accent-fg hover:text-enf-text-primary border border-enf-accent-fg/40 hover:border-enf-accent-fg rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
                <Share2 className="w-3.5 h-3.5" /> Share this PR
              </button>
            </div>
          ))}
        </div>

        <button onClick={onDone} data-testid="celebration-done"
          className="w-full mt-5 py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold transition-colors">
          Done — see my progress
        </button>
      </div>
    </div>
  );
};

// Program completion confirmation. Pure user-choice gate — the app detects
// the candidate, the user decides what counts. Per brand axiom: user defines
// how it matters. No mention of chains, no mention of Expert tier (Easter
// egg). Either confirm + log, or extend + snooze for 7 days.
const ProgramCompletionDialog = ({ candidate, onConfirm, onExtend }) => {
  if (!candidate) return null;
  return (
    <div className="fixed inset-0 z-[56] flex items-center justify-center p-4 bg-black/85" data-testid="program-completion-dialog">
      <div className="w-full max-w-md bg-enf-surface-deep border border-enf-accent-fg/40 rounded-xl p-6" style={{ boxShadow: '0 0 0 1px rgba(239,68,68,0.2), 0 30px 60px rgba(0,0,0,0.6)' }}>
        <div className="text-center mb-6">
          <div className="text-xs uppercase tracking-widest text-enf-accent-fg font-semibold mb-3 flex items-center justify-center gap-1.5">
            <Award className="w-4 h-4" /> {candidate.weeks} weeks in
          </div>
          <div className="font-display text-4xl tracking-wide leading-tight mb-2">Program complete.</div>
          <div className="text-base text-enf-text-secondary">Tempered to the next phase.</div>
        </div>

        <div className="space-y-2">
          <button onClick={onConfirm}
            data-testid="program-completion-confirm"
            className="w-full py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2">
            <Check className="w-4 h-4" /> Confirm completion
          </button>
          <button onClick={onExtend}
            data-testid="program-completion-extend"
            className="w-full py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold text-enf-text-secondary transition-colors">
            Not yet — I'm extending
          </button>
        </div>
      </div>
    </div>
  );
};

export default Strength;
